<template>
    <div ref="top" class="studio">
        <countdown
            v-if="requestTokenHeaders"
            class="uk-hidden"
            :end-time="new Date(requestTokenHeaders['expiry'] * 1000).getTime()"
            @finish="handleTokenExpiry">
            <template v-slot:process="countdown">
                {{ countdown.timeObj.ceil.s }}
            </template>
        </countdown>

        <Screensaver
            v-if="isTerminalMode && !isTerminalWithAds"
            @start="screensaverStart"
            :terminal="getInfos.terminal" />

        <div v-if="isTerminalMode">
            <Modal
                :title="$t('terminal.connector')"
                is-strict
                :id="modalIdTerminalConnectorChecker">
                <TerminalConnnectorNotRunnning />
            </Modal>
        </div>

        <div class="uk-hidden">
            <input
                id="pictures"
                ref="pictures"
                :accept="'.' + fileExtensions.join(', .')"
                multiple
                type="file"
                v-on:change="handleFilesUpload()"
                v-on:click="$refs.pictures.value = null" />
            <input
                ref="replacePicture"
                :accept="'.' + fileExtensions.join(', .')"
                type="file"
                v-on:change="replacePicture()" />
        </div>

        <header ref="nav" class="studio-nav">
            <div class="studio-container studio-nav-inner">
                <div
                    class="studio-brand"
                    v-touch:touchhold="showTerminalHandler">
                    <img
                        v-if="getInfos.logo"
                        :src="apiUrl + '/logos/' + getInfos.logo"
                        :alt="getInfos.name"
                        class="studio-logo" />
                    <span v-if="getInfos.name" class="studio-name">
                        {{ getInfos.name }}
                    </span>
                </div>

                <div class="studio-nav-end">
                    <template v-if="pictures.length > 0 && !successOrder">
                        <div class="studio-nav-total">
                            <span class="studio-muted uk-visible@s">
                                {{ getTotalSamplesCount }}
                                {{
                                    $t(
                                        getTotalSamplesCount === 1
                                            ? 'picture'
                                            : 'pictures'
                                    )
                                }}
                                ·
                            </span>
                            <strong>{{ getTotal }}</strong>
                        </div>
                        <Button
                            :action="goToCheckout"
                            :text="$t('studio.to.checkout')"
                            has-action
                            is-round
                            mode="primary" />
                    </template>
                    <Button
                        v-if="isTerminalMode"
                        :action="reload"
                        :text="$t('cancel')"
                        has-action
                        is-round
                        mode="default" />
                </div>
            </div>
            <progress
                v-if="isUploadInProgress"
                class="uk-progress studio-nav-progress"
                :value="getUnifiedUploadPercentage"
                max="100" />
        </header>

        <main class="studio-container studio-main">
            <div
                v-if="
                    getInfos.absence &&
                    isDateStringPast(getAbsenceStart(getInfos.absence.start)) &&
                    isDateStringFuture(getInfos.absence.end)
                "
                class="studio-notice"
                v-html="
                    $t('absence.message', {
                        start: getFormattedDateByDateString(
                            getInfos.absence.start
                        ),
                        end: getFormattedDateByDateString(getInfos.absence.end),
                    })
                " />

            <TerminalAdsHeader
                v-if="isTerminalWithAds"
                class="uk-margin-bottom"
                :terminal="getInfos.terminal" />

            <Card
                v-if="!getInfos.articles || getInfos.articles.length === 0"
                is-round
                is-dark
                mode="default">
                <div
                    v-if="isTerminalMode && !isTerminalConnectorRunning"
                    class="uk-flex uk-flex-center"
                    uk-spinner />
                <div v-else>{{ $t('article.none') }}</div>
            </Card>

            <template v-else-if="!successOrder">
                <section id="studio-pictures" class="studio-section">
                    <div class="studio-section-head is-start">
                        <h1 class="studio-title">
                            <template v-if="pictures.length > 0">
                                {{ pictures.length }}
                            </template>
                            {{
                                $t(pictures.length === 1 ? 'picture' : 'pictures')
                            }}
                        </h1>
                        <button
                            v-if="
                                pictures.length > 0 &&
                                !isTerminalMode &&
                                pictures.length < getInfos.maxPicturesPerOrder
                            "
                            type="button"
                            class="studio-add-button"
                            v-on:click="triggerInputDialog()">
                            <span class="studio-add-icon">
                                <Icon icon="plus" :ratio="0.7" />
                            </span>
                            {{ $t('studio.add.more') }}
                        </button>
                    </div>

                    <div v-if="isTerminalMode" class="studio-sources">
                        <div
                            v-if="terminalSources === null"
                            class="uk-flex uk-flex-center"
                            uk-spinner />
                        <InfoBox
                            v-else-if="terminalSources.length === 0"
                            mode="danger"
                            icon="warning">
                            <Translate translation-key="terminal.no.source" />
                        </InfoBox>
                        <div v-else class="studio-options">
                            <span
                                v-for="terminalSource in terminalSources"
                                :key="terminalSource"
                                v-on:click="
                                    selectTerminalSource(terminalSource)
                                ">
                                <LabelSelect is-round class="studio-source">
                                    <Icon
                                        :ratio="pictures.length > 0 ? 1.2 : 2"
                                        :icon="getIconByName(terminalSource)" />
                                    <div
                                        class="studio-strong uk-margin-small-top">
                                        <Translate
                                            :translation-key="terminalSource" />
                                    </div>
                                </LabelSelect>
                            </span>
                        </div>
                    </div>

                    <template v-if="pictures.length === 0 && !isTerminalMode">
                        <div
                            class="studio-dropzone"
                            v-on:click="triggerInputDialog()">
                            <Icon
                                :ratio="2.4"
                                class="studio-accent"
                                icon="cloud-upload" />
                            <div class="studio-dropzone-title">
                                <span class="studio-if-pointer">
                                    {{ $t('studio.empty.title') }}
                                </span>
                                <span class="studio-if-touch">
                                    {{ $t('studio.empty.touch.title') }}
                                </span>
                            </div>
                            <div class="studio-muted uk-margin-bottom">
                                <span class="studio-if-pointer">
                                    {{ $t('studio.empty.subtitle') }}
                                </span>
                                <span class="studio-if-touch">
                                    {{ $t('studio.empty.touch.subtitle') }}
                                </span>
                            </div>
                            <Button
                                :text="$t('studio.choose.pictures')"
                                is-round
                                is-large
                                mode="primary" />
                            <div
                                class="studio-muted studio-small uk-margin-top">
                                {{ uploadTypesText }}
                                · {{ $t('max') }} {{ getMaxFileSizeInMB }} MB
                                {{ $t('per.x', {x: $t('picture')}) }} ·
                                {{ $t('max') }}
                                {{ getInfos.maxPicturesPerOrder }}
                                {{ $t('pictures') }}
                            </div>
                            <div
                                v-if="picturesError"
                                class="input-error uk-margin-top">
                                {{ picturesError }}
                            </div>
                        </div>

                        <div class="studio-prices-head">
                            <button
                                type="button"
                                class="studio-add-button"
                                v-on:click="showPrices(true)">
                                <span class="studio-add-icon">
                                    <Icon icon="tag" :ratio="0.7" />
                                </span>
                                {{ $t('studio.formats.available') }}
                            </button>
                        </div>
                    </template>

                    <template v-if="pictures.length > 0">
                        <div v-if="pictures.length > 1" class="studio-bulk">
                            <span class="studio-small studio-muted">
                                {{ $t('studio.all.pictures') }}
                            </span>
                            <button type="button" class="studio-chip is-strong">
                                <span v-if="sharedArticleID">
                                    {{
                                        getArticleFormat(
                                            getArticleByID(sharedArticleID)
                                        )
                                    }}
                                    ·
                                    {{
                                        getPaperName(
                                            getArticleByID(sharedArticleID).paper
                                        )
                                    }}
                                </span>
                                <span v-else>{{ $t('format.and.paper') }}</span>
                                <Icon icon="chevron-down" :ratio="0.6" />
                            </button>
                            <div
                                class="studio-dropdown"
                                uk-dropdown="mode: click; pos: bottom-left; offset: 6; flip: false">
                                <div
                                    v-for="group in articleOverview"
                                    :key="'uo' + group.format"
                                    class="studio-overview-row">
                                    <div class="studio-overview-format">
                                        {{ group.format }}
                                    </div>
                                    <div class="studio-overview-papers">
                                        <button
                                            v-for="paper in group.papers"
                                            :key="'uop' + paper.value"
                                            type="button"
                                            :class="
                                                'studio-overview-paper' +
                                                (sharedArticleID === paper.value
                                                    ? ' active'
                                                    : '') +
                                                (paper.tierHint
                                                    ? ' has-tiers'
                                                    : '')
                                            "
                                            v-on:click="
                                                setUnifiedArticle(paper.value);
                                                applyBulk();
                                                hideDropdown($event);
                                            ">
                                            <span>{{
                                                getPaperName(paper.text)
                                            }}</span>
                                            <small>{{ paper.price }}</small>
                                            <span
                                                v-if="paper.tierHint"
                                                class="studio-overview-tiers">
                                                {{ paper.tierHint }}
                                            </span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="studio-grid">
                            <div
                                v-for="item in sortedPictures"
                                :key="'p' + item.id"
                                :id="'picture-' + item.id"
                                :class="
                                    'studio-tile' +
                                    (isPictureTooLength(item)
                                        ? ' has-warning'
                                        : '') +
                                    (item.id === newPictureId ? ' is-new' : '') +
                                    (item.error ? ' has-error' : '')
                                ">
                                <div
                                    class="studio-tile-stage"
                                    v-on:click="openEditor(item)">
                                    <div
                                        v-if="!item.coordinates && !item.error"
                                        class="studio-skeleton" />
                                    <div
                                        v-else-if="item.coordinates"
                                        class="studio-crop-frame"
                                        :style="getCropFrameStyle(item, 1.25)">
                                        <div
                                            :class="
                                                'studio-crop-box' +
                                                getPrintBorderClass(
                                                    item.originKey
                                                )
                                            "
                                            :style="getCropBoxStyle(item)" />
                                    </div>

                                    <div
                                        v-if="item.coordinates && !item.error"
                                        class="studio-tile-hover">
                                        <span>
                                            <Icon
                                                icon="crosshairs"
                                                :ratio="0.8" />
                                            {{ $t('studio.crop') }}
                                        </span>
                                    </div>

                                    <div
                                        class="studio-tile-tools"
                                        v-on:click.stop>
                                        <button
                                            v-if="item.coordinates && !item.error"
                                            type="button"
                                            class="studio-tile-crop"
                                            :aria-label="$t('studio.crop')"
                                            v-on:click="openEditor(item)">
                                            <Icon
                                                icon="crosshairs"
                                                :ratio="0.75" />
                                        </button>
                                        <button
                                            v-if="
                                                !item.cloneInProgress &&
                                                !item.error &&
                                                pictures.length <
                                                    getInfos.maxPicturesPerOrder
                                            "
                                            type="button"
                                            :uk-tooltip="
                                                'title:' + $t('duplicate')
                                            "
                                            :aria-label="$t('duplicate')"
                                            v-on:click="
                                                clonePicture(item.originKey)
                                            ">
                                            <Icon icon="copy" :ratio="0.75" />
                                        </button>
                                        <button
                                            type="button"
                                            :uk-tooltip="
                                                'title:' + $t('delete')
                                            "
                                            :aria-label="$t('delete')"
                                            v-on:click="
                                                deletePicture(item.originKey)
                                            ">
                                            <Icon icon="trash" :ratio="0.75" />
                                        </button>
                                    </div>

                                    <span
                                        v-if="isPictureTooLength(item)"
                                        class="studio-flag danger">
                                        <Icon icon="warning" :ratio="0.6" />
                                        {{ $t('studio.too.long') }}
                                    </span>
                                    <span
                                        v-else-if="isLowResolution(item)"
                                        class="studio-flag warning"
                                        :uk-tooltip="
                                            'title:' +
                                            $t('resolution.too.low.message')
                                        ">
                                        <Icon icon="warning" :ratio="0.6" />
                                        {{ $t('studio.low.resolution') }}
                                    </span>

                                    <progress
                                        v-if="!item.error && !item.isDone"
                                        class="uk-progress studio-tile-progress"
                                        :value="item.uploadPercentage"
                                        max="100" />

                                    <div
                                        v-if="item.error"
                                        class="studio-tile-error"
                                        v-on:click.stop>
                                        <Icon icon="warning" :ratio="1.1" />
                                        <span v-if="isUploadTypeError(item)">
                                            {{
                                                $t('files.type.error', {
                                                    name: $t('pictures'),
                                                    types: uploadTypesText,
                                                })
                                            }}
                                        </span>
                                        <span v-else>{{
                                            $t('studio.upload.failed')
                                        }}</span>
                                        <a
                                            v-if="!isUploadTypeError(item)"
                                            class="studio-link"
                                            v-on:click="retryUpload(item)">
                                            {{ $t('retry') }}
                                        </a>
                                    </div>
                                </div>

                                <div class="studio-tile-body">
                                    <div class="studio-chips">
                                        <button
                                            type="button"
                                            class="studio-chip is-strong">
                                            <span>
                                                {{ item.format }} ·
                                                {{
                                                    getPaperName(
                                                        getArticleByID(
                                                            item.articleID
                                                        ).paper
                                                    )
                                                }}
                                            </span>
                                            <Icon
                                                icon="chevron-down"
                                                :ratio="0.6" />
                                        </button>
                                        <div
                                            class="studio-dropdown"
                                            uk-dropdown="mode: click; pos: bottom-left; offset: 6; flip: false">
                                            <div
                                                v-for="group in articleOverview"
                                                :key="item.id + group.format"
                                                class="studio-overview-row">
                                                <div
                                                    class="studio-overview-format">
                                                    {{ group.format }}
                                                </div>
                                                <div
                                                    class="studio-overview-papers">
                                                    <button
                                                        v-for="paper in group.papers"
                                                        :key="
                                                            item.id + paper.value
                                                        "
                                                        type="button"
                                                        :class="
                                                            'studio-overview-paper' +
                                                            (item.articleID ===
                                                            paper.value
                                                                ? ' active'
                                                                : '') +
                                                            (paper.tierHint
                                                                ? ' has-tiers'
                                                                : '')
                                                        "
                                                        v-on:click="
                                                            setPictureArticle(
                                                                item.originKey,
                                                                paper.value
                                                            );
                                                            hideDropdown($event);
                                                        ">
                                                        <span>{{
                                                            getPaperName(
                                                                paper.text
                                                            )
                                                        }}</span>
                                                        <small>{{
                                                            paper.price
                                                        }}</small>
                                                        <span
                                                            v-if="paper.tierHint"
                                                            class="studio-overview-tiers">
                                                            {{ paper.tierHint }}
                                                        </span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        <template
                                            v-if="
                                                getPrintBorders(item.articleID)
                                                    .length > 0
                                            ">
                                            <button
                                                type="button"
                                                :class="
                                                    'studio-chip' +
                                                    (item.printBorderID
                                                        ? ''
                                                        : ' is-ghost')
                                                ">
                                                <Icon
                                                    v-if="!item.printBorderID"
                                                    icon="plus"
                                                    :ratio="0.6" />
                                                <span>{{
                                                    getPrintBorderByKey(
                                                        item.originKey
                                                    )
                                                        ? getPrintBorderTitle(
                                                              getPrintBorderByKey(
                                                                  item.originKey
                                                              )
                                                          )
                                                        : $t('studio.frame')
                                                }}</span>
                                                <Icon
                                                    v-if="item.printBorderID"
                                                    icon="chevron-down"
                                                    :ratio="0.6" />
                                            </button>
                                            <div
                                                class="studio-dropdown is-narrow"
                                                uk-dropdown="mode: click; pos: bottom-left; offset: 6; flip: false">
                                                <button
                                                    type="button"
                                                    :class="
                                                        'studio-overview-paper' +
                                                        (item.printBorderID
                                                            ? ''
                                                            : ' active')
                                                    "
                                                    v-on:click="
                                                        item.printBorderID = '';
                                                        hideDropdown($event);
                                                    ">
                                                    <span>{{
                                                        $t('without.frame')
                                                    }}</span>
                                                </button>
                                                <button
                                                    v-for="printBorder in getPrintBorders(
                                                        item.articleID
                                                    )"
                                                    :key="
                                                        item.id + printBorder.id
                                                    "
                                                    type="button"
                                                    :class="
                                                        'studio-overview-paper' +
                                                        (item.printBorderID ===
                                                        printBorder.id
                                                            ? ' active'
                                                            : '')
                                                    "
                                                    v-on:click="
                                                        item.printBorderID =
                                                            printBorder.id;
                                                        hideDropdown($event);
                                                    ">
                                                    <span
                                                        class="studio-swatch"
                                                        :style="{
                                                            background:
                                                                printBorder.color,
                                                        }" />
                                                    <span>{{
                                                        getPrintBorderTitle(
                                                            printBorder
                                                        )
                                                    }}</span>
                                                </button>
                                            </div>
                                        </template>
                                    </div>

                                    <div
                                        v-if="
                                            isVariableHeight(item.originKey) &&
                                            item.measurementValue
                                        "
                                        :class="
                                            'studio-small' +
                                            (isPictureTooLength(item)
                                                ? ' studio-danger'
                                                : ' studio-muted')
                                        ">
                                        {{ $t('pictureLength') }}
                                        {{
                                            formatMeasurement(
                                                item.measurementValue
                                            )
                                        }}
                                        {{ $t(getInfos.measurementUnit) }}
                                        <span v-if="isPictureTooLength(item)">
                                            ({{ $t('max') }}
                                            {{
                                                getArticleByID(item.articleID)
                                                    .maxLength
                                            }})
                                        </span>
                                    </div>

                                    <div class="studio-tile-footer">
                                        <div class="studio-stepper">
                                            <button
                                                type="button"
                                                :disabled="item.amount <= 1"
                                                :aria-label="
                                                    $t('amount') + ' -1'
                                                "
                                                v-on:click="
                                                    manipulateAmount(
                                                        item.originKey,
                                                        -1
                                                    )
                                                ">
                                                <Icon
                                                    icon="minus"
                                                    :ratio="0.7" />
                                            </button>
                                            <input
                                                v-model.number="item.amount"
                                                type="number"
                                                min="1"
                                                :aria-label="$t('amount')"
                                                v-on:input="
                                                    validateAmount(
                                                        item.originKey
                                                    )
                                                " />
                                            <button
                                                type="button"
                                                :aria-label="
                                                    $t('amount') + ' +1'
                                                "
                                                v-on:click="
                                                    manipulateAmount(
                                                        item.originKey,
                                                        +1
                                                    )
                                                ">
                                                <Icon
                                                    icon="plus"
                                                    :ratio="0.7" />
                                            </button>
                                        </div>
                                        <div class="studio-tile-price">
                                            <div class="studio-strong">
                                                {{
                                                    getNumberWithCurrency(
                                                        getCostsByAmountAndArticleId(
                                                            item
                                                        )
                                                    )
                                                }}
                                            </div>
                                            <small
                                                v-if="getNextTierHint(item)"
                                                class="studio-tier-hint">
                                                {{ getNextTierHint(item) }}
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div
                                v-if="
                                    !isTerminalMode &&
                                    pictures.length <
                                        getInfos.maxPicturesPerOrder
                                "
                                class="studio-tile studio-tile-add"
                                v-on:click="triggerInputDialog()">
                                <Icon icon="plus" :ratio="1.4" />
                                <span>{{ $t('studio.add.more') }}</span>
                                <small
                                    v-if="isDeviceMobile && isUploadInProgress">
                                    {{ $t('upload.in.progress.hint') }}
                                </small>
                            </div>
                        </div>

                        <InfoBox
                            v-if="
                                pictures.length >= getInfos.maxPicturesPerOrder
                            "
                            class="uk-margin-top"
                            mode="warning">
                            {{
                                $t('pictures.max.reached', {
                                    x: getInfos.maxPicturesPerOrder,
                                })
                            }}
                        </InfoBox>
                    </template>
                </section>

                <section
                    v-if="pictures.length > 0"
                    id="studio-checkout"
                    class="studio-section">
                    <div class="studio-checkout">
                        <div class="studio-section-head">
                            <h1 class="studio-title">{{ $t('order') }}</h1>
                        </div>
                        <Card
                            is-round
                            is-dark
                            mode="default"
                            class="studio-checkout-card">
                            <div id="checkout-contact" class="studio-block">
                                <div class="studio-card-title">
                                    {{ $t('contact.details') }}
                                </div>
                                <div class="uk-grid-small" uk-grid>
                                    <div
                                        v-for="(
                                            inputField, key
                                        ) in contactFields"
                                        :key="'i' + inputField.name"
                                        :class="
                                            key === 0
                                                ? 'uk-width-1-1'
                                                : 'uk-width-1-2@s uk-width-1-1'
                                        ">
                                        <TextField
                                            v-model="inputField.value"
                                            is-round
                                            :autocomplete="
                                                inputField.autocomplete
                                            "
                                            :field-key="key"
                                            :icon="inputField.icon"
                                            :is-optional="inputField.optional"
                                            :label="inputField.label"
                                            :name="inputField.name"
                                            :on-enter="complete"
                                            :rules="inputField.rules"
                                            :type="inputField.type"
                                            :disabled="
                                                successOrder || loadingOrder
                                            "
                                            :validate="triggerContactFields"
                                            @setValid="setValidContactFields"
                                            :has-keyboard="isTerminalMode" />
                                    </div>
                                </div>
                            </div>

                            <div id="checkout-delivery" class="studio-block">
                                <div class="studio-card-title">
                                    {{ $t('delivery.type') }}
                                </div>
                                <div class="studio-options wide">
                                    <span
                                        v-for="method in getDeliveryTypeOptions"
                                        :key="method"
                                        v-on:click="setDeliveryTypes(method)">
                                        <LabelSelect
                                            is-round
                                            fill-selected
                                            class="lunacart-theme-selected"
                                            :is-disabled="
                                                successOrder || loadingOrder
                                            "
                                            :is-selected="
                                                deliveryType === method
                                            ">
                                            <div
                                                class="uk-text-center studio-option">
                                                <Icon
                                                    :icon="
                                                        getIconByName(method)
                                                    "
                                                    :ratio="1.2" />
                                                <div class="paper-text">
                                                    {{ $t(method) }}
                                                </div>
                                                <div class="paper-price">
                                                    {{
                                                        method === 'shipping'
                                                            ? getNumberWithCurrency(
                                                                  getShippingPrice()
                                                              )
                                                            : $t('studio.free')
                                                    }}
                                                </div>
                                            </div>
                                        </LabelSelect>
                                    </span>
                                </div>

                                <div
                                    v-if="deliveryType === 'pickup'"
                                    class="uk-margin-medium-top">
                                    <StoreSelect
                                        v-model="storeId"
                                        :is-error="triggerTerms > 0" />
                                </div>

                                <div
                                    v-if="isAddressShown"
                                    class="uk-margin-medium-top">
                                    <div class="uk-grid-small" uk-grid>
                                        <div
                                            v-for="(
                                                inputField, key
                                            ) in addressFields"
                                            :key="'ia' + inputField.name"
                                            :class="
                                                key > 0
                                                    ? 'uk-width-1-2@s uk-width-1-1'
                                                    : 'uk-width-1-1'
                                            ">
                                            <TextField
                                                v-model="inputField.value"
                                                is-round
                                                :autocomplete="
                                                    inputField.autocomplete
                                                "
                                                :field-key="key"
                                                :icon="inputField.icon"
                                                :is-optional="
                                                    inputField.optional
                                                "
                                                :label="inputField.label"
                                                :name="inputField.name"
                                                :on-enter="complete"
                                                :rules="inputField.rules"
                                                :type="inputField.type"
                                                :disabled="
                                                    successOrder || loadingOrder
                                                "
                                                :validate="triggerAddressFields"
                                                @setValid="
                                                    setValidAddressFields
                                                "
                                                :has-keyboard="
                                                    isTerminalMode
                                                " />
                                        </div>
                                        <div class="uk-width-1-1">
                                            <LabelWithError
                                                :label="$t('country') + '*'" />
                                            <form autocomplete="off">
                                                <select
                                                    v-model="country"
                                                    name="country"
                                                    :disabled="
                                                        getInfos
                                                            .shippingCountries
                                                            .length === 1
                                                    "
                                                    class="uk-select uk-input-styled studio-round">
                                                    <option
                                                        v-for="option in getInfos.shippingCountries"
                                                        :key="option"
                                                        :value="option">
                                                        {{ $t(option) }}
                                                    </option>
                                                </select>
                                            </form>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div id="payment-method" class="studio-block">
                                <div class="studio-card-title">
                                    {{ $t('payment.method') }}
                                </div>
                                <div class="studio-options wide">
                                    <span
                                        v-for="method in getPaymentMethodOptions"
                                        :key="method"
                                        v-on:click="setPaymentMethod(method)">
                                        <LabelSelect
                                            is-round
                                            fill-selected
                                            class="lunacart-theme-selected"
                                            :is-disabled="
                                                successOrder || loadingOrder
                                            "
                                            :is-selected="
                                                paymentMethod === method
                                            ">
                                            <div
                                                class="uk-text-center studio-option">
                                                <Icon
                                                    :icon="
                                                        getIconByName(method)
                                                    "
                                                    :ratio="1.2" />
                                                <div class="paper-text">
                                                    {{ $t(method) }}
                                                </div>
                                            </div>
                                        </LabelSelect>
                                    </span>
                                </div>

                                <div
                                    class="uk-margin-medium-top"
                                    v-if="paymentMethod === 'online'">
                                    <div
                                        v-if="
                                            paymentPickRequired && !paymentPicked
                                        "
                                        class="studio-danger studio-payment-pick">
                                        {{ $t('studio.payment.pick') }}
                                    </div>
                                    <stripe-element-payment
                                        v-if="paymentKey"
                                        :key="elementsOptions.clientSecret"
                                        :create-options="paymentCreateOptions"
                                        @element-ready="setPaymentRendered"
                                        @element-change="onElementChange"
                                        ref="paymentRef"
                                        :pk="getInfos.stripe.key"
                                        :elements-options="elementsOptions"
                                        :confirm-params="confirmParams" />
                                    <div
                                        v-else
                                        class="uk-flex uk-flex-center"
                                        uk-spinner />
                                </div>
                            </div>

                            <div class="studio-block">
                                <div class="studio-card-title">
                                    {{ $t('comments') }}
                                    <span class="studio-muted">
                                        ({{ $t('optional') }})
                                    </span>
                                </div>
                                <TextArea
                                    v-model="comments"
                                    :is-disabled="successOrder || loadingOrder"
                                    is-round
                                    is-label-hidden
                                    label="comments"
                                    name="comments"
                                    :has-keyboard="isTerminalMode"
                                    rules="max:4096" />
                            </div>

                            <div class="studio-block">
                                <div class="studio-card-title">
                                    {{ $t('studio.order.summary') }}
                                </div>
                                <div
                                    v-for="row in summaryRows"
                                    :key="'sr' + row.articleID"
                                    class="studio-summary-row">
                                    <div class="uk-grid-small" uk-grid>
                                        <div class="uk-width-expand" uk-leader>
                                            <span class="uk-badge badge">{{
                                                row.amount
                                            }}</span>
                                            {{ row.label }}
                                        </div>
                                        <div>
                                            {{
                                                getNumberWithCurrency(row.total)
                                            }}
                                        </div>
                                    </div>
                                    <div
                                        v-if="row.hint"
                                        class="studio-hint-tag">
                                        <Icon icon="tag" :ratio="0.65" />
                                        {{ row.hint }}
                                    </div>
                                </div>

                                <div
                                    v-if="getFeeBySource() > 0"
                                    key="summary-fee"
                                    class="studio-summary-row is-extra">
                                    <div class="uk-grid-small" uk-grid>
                                        <div class="uk-width-expand" uk-leader>
                                            {{ $t('service.fee') }}
                                        </div>
                                        <div>
                                            {{ getNumberWithCurrency(getFee) }}
                                        </div>
                                    </div>
                                    <div
                                        v-if="getFeeDiscountBySource() != null"
                                        class="studio-small studio-muted">
                                        {{ getFeeDiscountText }}
                                    </div>
                                </div>

                                <div
                                    v-if="deliveryType === 'shipping'"
                                    key="summary-shipping"
                                    class="studio-summary-row is-extra">
                                    <div class="uk-grid-small" uk-grid>
                                        <div class="uk-width-expand" uk-leader>
                                            {{ $t('shipping.costs') }}
                                        </div>
                                        <div>
                                            {{
                                                getNumberWithCurrency(
                                                    getShippingCosts
                                                )
                                            }}
                                        </div>
                                    </div>
                                </div>

                                <div
                                    key="summary-total"
                                    class="uk-grid-small total-costs-text"
                                    uk-grid>
                                    <div class="uk-width-expand" uk-leader>
                                        {{ $t('total') }}
                                    </div>
                                    <div>{{ getTotal }}</div>
                                </div>
                                <div class="vat">
                                    {{
                                        $t('vat.message', {
                                            vat: getNumberFormatted(
                                                getInfos.vat,
                                                0
                                            ),
                                        })
                                    }}
                                </div>
                            </div>

                            <div id="checkout-terms" class="studio-block">
                                <div class="studio-card-title">
                                    {{ $t('confirm') }}
                                </div>
                                <Checkbox
                                    v-model="confirmTerms"
                                    class="studio-terms"
                                    is-round
                                    :disabled="successOrder || loadingOrder"
                                    :is-disabled="successOrder || loadingOrder"
                                    :validate="triggerTerms"
                                    label="termsAndConditions.confirm"
                                    name="terms"
                                    rules="required"
                                    @setValid="setTermsValid" />

                            <InfoBox
                                v-if="isUploadInProgress"
                                class="uk-margin-bottom uk-animation-fade"
                                mode="warning">
                                {{ $t('upload.in.progress') }}
                                <progress
                                    :value="getUnifiedUploadPercentage"
                                    class="uk-progress studio-progress"
                                    max="100" />
                            </InfoBox>

                            <InfoBox
                                v-if="isAnyUploadError"
                                class="uk-margin-bottom uk-animation-fade"
                                mode="danger">
                                {{ $t('any.pictures.error') }}
                                <div class="uk-margin-small-top" />
                                <Button
                                    :action="retryFailedPictures"
                                    :text="$t('retry')"
                                    has-action
                                    is-round
                                    mode="primary" />
                            </InfoBox>

                            <Button
                                :action="complete"
                                :is-disabled="
                                    isUploadInProgress ||
                                    isAnyUploadError ||
                                    isPaymentInProgress ||
                                    !paymentRendered
                                "
                                :is-loading="loadingOrder"
                                :text="$t('paid.order')"
                                class="studio-pay"
                                has-action
                                is-round
                                is-large
                                is-max
                                mode="success">
                                <span class="studio-pay-amount">
                                    {{ getTotal }}
                                </span>
                            </Button>

                            <div v-if="errorOrder" class="uk-margin-top" />
                            <ErrorMessage
                                :custom-errors="[
                                    {
                                        code: 408,
                                        message: $t('session.timed.out'),
                                    },
                                ]"
                                :error="errorOrder" />

                            <InfoBox
                                v-if="paymentError"
                                class="uk-margin-top uk-animation-fade"
                                mode="danger">
                                <Translate
                                    translation-key="payment.denied.error" />
                            </InfoBox>
                            </div>
                        </Card>
                    </div>
                </section>
            </template>

            <Card
                v-else
                is-round
                is-dark
                mode="default"
                class="studio-success uk-text-center">
                <div class="studio-success-icon">
                    <Icon icon="check" :ratio="2" />
                </div>
                <div class="studio-title">{{ $t('order.created.title') }}</div>
                <div class="studio-muted uk-margin-small-top">
                    {{ $t('paid.order.success') }}
                </div>
                <div class="uk-margin-medium-top" />
                <Button
                    :action="reload"
                    :text="$t('order.more.pictures')"
                    has-action
                    is-round
                    is-large
                    mode="primary" />

                <div
                    v-if="!isApp && !isTerminalMode"
                    class="uk-text-left uk-margin-medium-top">
                    <PWAPromptForm
                        v-if="getInfos && getInfos.name"
                        :name="getInfos.name"
                        :prompt-installer-event="promptInstallerEvent" />
                    <PWAHintForm
                        v-if="getInfos && getInfos.name"
                        :name="getInfos.name" />
                </div>
            </Card>

            <TerminalAdsFooter
                v-if="isTerminalWithAds && getInfos.terminal.ads"
                class="uk-margin-top"
                :terminal="getInfos.terminal" />
        </main>

        <footer class="studio-footer">
            <div class="studio-container">
                <span v-if="getInfos.footerText">{{
                    getInfos.footerText
                }}</span>
                <ToggleItem is-dark toggle="target: #termsAndConditions">
                    {{ $t('termsAndConditions') }}
                </ToggleItem>
                <ToggleItem is-dark toggle="target: #dataPrivacy">
                    {{ $t('dataPrivacy') }}
                </ToggleItem>
                <ToggleItem is-dark toggle="target: #siteNotice">
                    {{ $t('siteNotice') }}
                </ToggleItem>
                <ToggleItem
                    v-if="isCookieSettingsShown"
                    is-dark
                    v-on:click.native="openCookieSettings()">
                    {{ $t('cookies.settings') }}
                </ToggleItem>
                <LanguageSelector id="studio" is-round />
            </div>
        </footer>

        <transition name="studio-editor" :duration="300">
            <div
                v-if="isPricesShown"
                class="studio-editor"
                v-on:mousedown.self="showPrices(false)">
                <div class="studio-editor-panel is-auto">
                    <div class="studio-editor-bar">
                        <div class="studio-editor-title">
                            <div>{{ $t('studio.formats.available') }}</div>
                            <div class="studio-editor-sub">
                                {{ $t('studio.formats.hint') }}
                            </div>
                        </div>
                        <button
                            type="button"
                            class="studio-round-button"
                            :aria-label="$t('close')"
                            v-on:click="showPrices(false)">
                            <Icon icon="close" />
                        </button>
                    </div>
                    <div class="studio-prices">
                        <div
                            v-for="group in articleOverview"
                            :key="'pl' + group.format"
                            class="studio-price">
                            <div class="studio-price-format">
                                {{ group.format }}
                            </div>
                            <template v-for="paper in group.papers">
                                <div
                                    :key="'plp' + paper.value"
                                    class="studio-price-row">
                                    <span>{{ getPaperName(paper.text) }}</span>
                                    <span class="studio-strong">
                                        {{ paper.price }}
                                    </span>
                                </div>
                                <div
                                    v-if="paper.tierHint"
                                    :key="'plt' + paper.value"
                                    class="studio-overview-tiers">
                                    {{ paper.tierHint }}
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

        <transition name="studio-editor" :duration="300">
            <div
                v-if="editorPicture"
                class="studio-editor"
                v-on:mousedown.self="closeEditor">
                <div class="studio-editor-panel">
                    <div class="studio-editor-bar">
                        <div class="studio-editor-title">
                            <div>{{ $t('studio.crop') }}</div>
                            <div class="studio-editor-sub">
                                {{
                                    $t('studio.picture.x.of.y', {
                                        x: editorIndex + 1,
                                        y: sortedPictures.length,
                                    })
                                }}
                                · {{ editorPicture.format }} ·
                                {{
                                    getPaperName(
                                        getArticleByID(editorPicture.articleID).paper
                                    )
                                }}
                            </div>
                        </div>
                        <div class="studio-editor-nav">
                            <span
                                :uk-tooltip="
                                    'title:' +
                                    $t(
                                        editorIndex <= 0
                                            ? 'studio.picture.first'
                                            : 'studio.picture.previous'
                                    )
                                ">
                                <button
                                    type="button"
                                    class="studio-round-button"
                                    :disabled="editorIndex <= 0"
                                    :aria-label="$t('previous')"
                                    v-on:click="editorGo(-1)">
                                    <Icon icon="chevron-left" />
                                </button>
                            </span>
                            <span
                                :uk-tooltip="
                                    'title:' +
                                    $t(
                                        editorIndex >= sortedPictures.length - 1
                                            ? 'studio.picture.last'
                                            : 'studio.picture.next'
                                    )
                                ">
                                <button
                                    type="button"
                                    class="studio-round-button"
                                    :disabled="
                                        editorIndex >= sortedPictures.length - 1
                                    "
                                    :aria-label="$t('next')"
                                    v-on:click="editorGo(1)">
                                    <Icon icon="chevron-right" />
                                </button>
                            </span>
                            <button
                                type="button"
                                class="studio-round-button studio-editor-close"
                                :aria-label="$t('close')"
                                :uk-tooltip="'title:' + $t('close')"
                                v-on:click="closeEditor()">
                                <Icon icon="close" />
                            </button>
                        </div>
                    </div>

                    <div class="studio-editor-stage">
                        <cropper
                            v-if="editorPicture.coordinates"
                            :key="editorCropperKey"
                            ref="editorCropper"
                            class="studio-cropper"
                            :src="editorPicture.thumbnailUrl"
                            :debounce="300"
                            :move-image="true"
                            :auto-zoom="false"
                            :transitions="true"
                            default-boundaries="fit"
                            :resize-image="{
                                wheel: true,
                                adjustStencil: true,
                                touch: true,
                            }"
                            :default-size="editorDefaultSize"
                            :default-position="editorDefaultPosition"
                            :style="getPrintBorderVars(editorKey)"
                            :stencil-props="{
                                aspectRatio: getCropperRatioByPictureKey(editorKey),
                                class: 'cropper-stencil',
                                previewClass:
                                    'cropper-stencil__preview' +
                                    getPrintBorderClass(editorKey, true),
                                draggingClass: 'cropper-stencil--dragging',
                            }"
                            @change="onEditorCropperChange" />
                        <div v-else uk-spinner />
                    </div>

                    <div class="studio-editor-tools">
                        <div
                            v-if="isOrientationSwitchable(editorKey)"
                            class="studio-segmented">
                            <button
                                type="button"
                                :class="
                                    getPictureFormatName(editorKey) === 'vertical'
                                        ? 'active'
                                        : ''
                                "
                                v-on:click="setOrientation(editorKey, 'vertical')">
                                <Icon icon="phone" :ratio="0.8" />
                                {{ $t('vertical.format') }}
                            </button>
                            <button
                                type="button"
                                :class="
                                    getPictureFormatName(editorKey) === 'horizontal'
                                        ? 'active'
                                        : ''
                                "
                                v-on:click="setOrientation(editorKey, 'horizontal')">
                                <Icon icon="phone-landscape" :ratio="0.8" />
                                {{ $t('horizontal.format') }}
                            </button>
                        </div>
                        <div
                            v-if="getPrintBorders(editorPicture.articleID).length > 0"
                            class="studio-editor-frame">
                            <button
                                type="button"
                                :class="
                                    'studio-chip' +
                                    (editorPicture.printBorderID ? '' : ' is-ghost')
                                ">
                                <Icon
                                    v-if="!editorPicture.printBorderID"
                                    icon="plus"
                                    :ratio="0.6" />
                                <span>{{
                                    getPrintBorderByKey(editorKey)
                                        ? getPrintBorderTitle(
                                              getPrintBorderByKey(editorKey)
                                          )
                                        : $t('studio.frame')
                                }}</span>
                                <Icon
                                    v-if="editorPicture.printBorderID"
                                    icon="chevron-down"
                                    :ratio="0.6" />
                            </button>
                            <div
                                class="studio-dropdown is-narrow"
                                uk-dropdown="mode: click; pos: top-left; offset: 6">
                                <button
                                    type="button"
                                    :class="
                                        'studio-overview-paper' +
                                        (editorPicture.printBorderID ? '' : ' active')
                                    "
                                    v-on:click="
                                        editorPicture.printBorderID = '';
                                        hideDropdown($event);
                                    ">
                                    <span>{{ $t('without.frame') }}</span>
                                </button>
                                <button
                                    v-for="printBorder in getPrintBorders(
                                        editorPicture.articleID
                                    )"
                                    :key="'eb' + printBorder.id"
                                    type="button"
                                    :class="
                                        'studio-overview-paper' +
                                        (editorPicture.printBorderID ===
                                        printBorder.id
                                            ? ' active'
                                            : '')
                                    "
                                    v-on:click="
                                        editorPicture.printBorderID = printBorder.id;
                                        hideDropdown($event);
                                    ">
                                    <span
                                        class="studio-swatch"
                                        :style="{background: printBorder.color}" />
                                    <span>{{
                                        getPrintBorderTitle(printBorder)
                                    }}</span>
                                </button>
                            </div>
                        </div>
                        <button
                            type="button"
                            class="studio-soft-button"
                            v-on:click="resetCroppingByKey(editorKey)">
                            <Icon icon="refresh" :ratio="0.8" />
                            {{ $t('studio.crop.reset') }}
                        </button>
                        <span class="studio-editor-hint">
                            <span class="studio-if-pointer">
                                {{ $t('studio.crop.hint') }}
                            </span>
                            <span class="studio-if-touch">
                                {{ $t('studio.crop.hint.touch') }}
                            </span>
                        </span>
                        <span
                            v-if="
                                isVariableHeight(editorKey) &&
                                editorPicture.measurementValue
                            "
                            :class="
                                'studio-small' +
                                (isPictureTooLength(editorPicture)
                                    ? ' studio-danger'
                                    : ' studio-strong')
                            ">
                            {{ $t('pictureLength') }}
                            {{ formatMeasurement(editorPicture.measurementValue) }}
                            {{ $t(getInfos.measurementUnit) }}
                            <span v-if="isPictureTooLength(editorPicture)">
                                ({{ $t('max') }}
                                {{
                                    getArticleByID(editorPicture.articleID)
                                        .maxLength
                                }})
                            </span>
                        </span>
                    </div>
                </div>
            </div>
        </transition>

        <div
            v-if="isDraggingFiles"
            class="studio-dropoverlay"
            v-on:dragover.prevent
            v-on:dragleave="isDraggingFiles = false"
            v-on:drop.prevent="dropListener">
            <div class="studio-dropoverlay-box">
                <Icon icon="cloud-upload" :ratio="3" />
                <div>{{ $t('studio.drop.release') }}</div>
            </div>
        </div>

        <div
            v-if="isTerminalMode"
            :id="modalIdTerminalSelect"
            class="uk-modal-full"
            uk-modal>
            <div class="uk-modal-dialog">
                <div class="uk-modal-header">
                    <ul
                        uk-switcher="animation: uk-animation-fade"
                        class="uk-child-width-expand"
                        id="sources">
                        <li
                            v-for="tab in terminalSources"
                            :key="tab"
                            :class="
                                tab === selectedTerminalSource
                                    ? 'uk-active'
                                    : ''
                            "
                            v-on:click="selectTerminalSource(tab)">
                            <a href="#">
                                <Icon :icon="getIconByName(tab)" />
                                <Translate :translation-key="tab" />
                            </a>
                        </li>
                    </ul>

                    <ul class="uk-breadcrumb">
                        <li
                            v-for="(dir, key) in partedTerminalPaths"
                            v-on:click="selectDir(key)"
                            :key="'d' + dir">
                            <a href="#" :uk-tooltip="'title:' + dir">{{
                                getSlimDirName(dir)
                            }}</a>
                        </li>
                    </ul>
                </div>

                <div class="uk-modal-body" uk-overflow-auto>
                    <ul class="uk-switchers uk-padding-remove-left">
                        <li
                            v-for="terminalSource in [selectedTerminalSource]"
                            :key="terminalSource">
                            <div
                                v-if="terminalFilesError"
                                class="terminal-content-loading device-in-info uk-flex uk-flex-center uk-flex-middle">
                                <Translate
                                    translation-key="medium.missing.message"
                                    :parameter="{
                                        x: $t(selectedTerminalSource),
                                    }" />
                            </div>
                            <div
                                v-else-if="
                                    terminalFilesLoading ||
                                    terminalSource !== selectedTerminalSource
                                "
                                class="terminal-content-loading uk-flex uk-flex-center uk-flex-middle">
                                <div class="device-in-info" uk-spinner />
                            </div>
                            <div v-else>
                                <div
                                    uk-grid
                                    class="uk-grid-small uk-child-width-1-6@l uk-child-width-1-5@m uk-child-width-1-4@s uk-child-width-1-2 terminal-content">
                                    <div v-if="terminalFilesDir !== ''">
                                        <div
                                            class="uk-card uk-card-default uk-card-hover cursor-pointer">
                                            <div
                                                v-on:click="
                                                    backTerminalFolder()
                                                "
                                                class="uk-height-small uk-text-center folder-struct uk-padding-small">
                                                <Icon
                                                    icon="arrow-left"
                                                    :ratio="2.5" />
                                                <div
                                                    class="action-name uk-margin-small-top folder-name">
                                                    <Translate
                                                        translation-key="back" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div
                                        v-for="(
                                            terminalFile, key
                                        ) in terminalFiles"
                                        :key="'tf' + key">
                                        <div
                                            class="uk-card uk-card-default uk-card-hover cursor-pointer">
                                            <div
                                                v-if="terminalFile.isDir"
                                                v-on:click="
                                                    selectTerminalFolder(
                                                        terminalFile.path,
                                                        terminalFile.name
                                                    )
                                                "
                                                class="uk-height-small uk-text-center folder-struct uk-padding-small">
                                                <Icon
                                                    icon="folder"
                                                    :ratio="2.5" />

                                                <div
                                                    :uk-tooltip="
                                                        'title:' +
                                                        terminalFile.name
                                                    "
                                                    class="action-name uk-margin-small-top folder-name">
                                                    {{ terminalFile.name }}
                                                </div>
                                            </div>

                                            <div
                                                v-else
                                                v-on:click="
                                                    toggleFileSelect(key)
                                                "
                                                class="uk-cover-container uk-height-small">
                                                <div
                                                    class="uk-flex uk-flex-center uk-flex-middle image-item">
                                                    <img
                                                        uk-img
                                                        class="image-item-struct"
                                                        :src="
                                                            apiUrlTerminal +
                                                            '/thumbnails/' +
                                                            terminalFile.device +
                                                            terminalFile.path +
                                                            '/' +
                                                            terminalFile.name
                                                        "
                                                        :alt="
                                                            terminalFile.name
                                                        " />
                                                </div>

                                                <div
                                                    v-if="isFileSelected(key)"
                                                    class="uk-overlay uk-overlay-primary uk-position-cover">
                                                    <div
                                                        class="uk-flex uk-flex-middle uk-flex-center uk-height-1-1">
                                                        <Icon
                                                            class="uk-form-success"
                                                            icon="check"
                                                            :ratio="1.5" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </li>
                    </ul>
                </div>

                <div class="uk-modal-footer">
                    <div class="uk-grid uk-child-width-expand" uk-grid>
                        <div>
                            <Button
                                mode="default"
                                is-max
                                has-action
                                :action="cancelSelection"
                                is-large>
                                <Translate translation-key="cancel" />
                            </Button>
                        </div>
                        <div>
                            <Button
                                mode="default"
                                is-max
                                has-action
                                :action="selectAllInFolder"
                                is-large>
                                <Translate translation-key="select.all" />
                            </Button>
                        </div>
                        <div>
                            <Button
                                mode="primary"
                                is-max
                                has-action
                                :is-loading="loadingFetch"
                                :action="processSelectedPictures"
                                is-large>
                                <span v-if="selectedTerminalFiles.length > 0">
                                    {{ selectedTerminalFiles.length }}
                                </span>
                                <Translate
                                    translation-key="x.add"
                                    :parameter="{
                                        x: $t(
                                            selectedTerminalFiles.length === 1
                                                ? 'picture'
                                                : 'pictures'
                                        ),
                                    }" />
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <TerminalAdsScreensaver
            v-if="isTerminalWithAds && successOrder"
            :pictures="sortedPictures"
            @ended="reload"
            :terminal="getInfos.terminal" />

        <Modal
            is-round
            id="termsAndConditions"
            :title="$t('termsAndConditions')"
            is-extended>
            <div v-html="getInfos.termsAndConditions" />
        </Modal>
        <Modal is-round id="dataPrivacy" :title="$t('dataPrivacy')" is-extended>
            <div v-html="getInfos.dataPrivacy" />
        </Modal>
        <Modal is-round id="siteNotice" :title="$t('siteNotice')" is-extended>
            <div v-html="getInfos.siteNotice" />
        </Modal>

        <Modal :id="modalIdSmartphone" :title="$t('smartphone.qrcode')">
            <div class="uk-text-center">
                <Description>
                    <Translate translation-key="smartphone.qrcode.message" />
                </Description>
                <div ref="qrcode" />
            </div>
        </Modal>
    </div>
</template>

<script>
import QRCode from 'easyqrcodejs';
import TextField from '@/components/generic/TextField';
import Card from '@/components/generic/Card';
import LabelSelect from '@/components/LabelSelect';
import LabelWithError from '@/components/generic/LabelWithError';
import Checkbox from '@/components/generic/Checkbox.vue';
import StoreSelect from '@/components/StoreSelect.vue';
import ToggleItem from '@/components/generic/ToggleItem';
import Button from '@/components/generic/Button';
import UIkit from 'uikit';
import Icon from '@/components/generic/Icon';
import InfoBox from '@/components/generic/InfoBox';
import axios from 'axios';
import {v4 as uuidv4} from 'uuid';
import ErrorMessage from '@/components/generic/ErrorMessage';
import TextArea from '@/components/TextArea';
import {isMobileOnly} from 'mobile-device-detect';
import Vue from 'vue';
import Modal from '../components/generic/Modal.vue';
import dayjs from 'dayjs';
import PWAHintForm from '@/components/PWAHintForm.vue';
import PWAPromptForm from '@/components/PWAPromptForm.vue';
import Translate from '@/components/Translate.vue';
import Screensaver from '@/components/Screensaver.vue';
import TerminalConnnectorNotRunnning from '@/components/TerminalConnnectorNotRunnning.vue';
import TerminalAdsHeader from '@/components/TerminalAdsHeader.vue';
import TerminalAdsFooter from '@/components/TerminalAdsFooter.vue';
import Description from '@/components/Description.vue';
import LanguageSelector from '@/components/LanguageSelector.vue';
import TerminalAdsScreensaver from '@/components/TerminalAdsScreensaver.vue';
import {StripeElementPayment} from '@vue-stripe/vue-stripe';

export default {
    name: 'Main',
    components: {
        Card,
        LabelSelect,
        LabelWithError,
        Checkbox,
        StoreSelect,
        ToggleItem,
        LanguageSelector,
        StripeElementPayment,
        TerminalAdsScreensaver,
        Description,
        TerminalAdsFooter,
        TerminalAdsHeader,
        TerminalConnnectorNotRunnning,
        Screensaver,
        Translate,
        PWAPromptForm,
        PWAHintForm,
        Modal,
        TextArea,
        ErrorMessage,
        InfoBox,
        Icon,
        Button,
        TextField,
    },
    data() {
        return {
            paymentRendered: false,
            paymentCompleted: false,
            paymentPicked: false,
            paymentPickRequired: false,

            paymentKey: false,
            elementsOptions: {
                appearance: {
                    variables: {
                        colorPrimary: '#092633',
                        colorBackground: '#ffffff',
                        colorText: '#30313d',
                        colorDanger: '#df1b41',
                        borderRadius: '12px',
                    },
                },
            },
            // a list with radios: tabs put every method that doesn't fit into a
            // dropdown. Collapsed, so the card form doesn't push the others down
            paymentCreateOptions: {
                layout: {
                    type: 'accordion',
                    defaultCollapsed: true,
                    radios: true,
                    spacedAccordionItems: true,
                },
            },
            confirmParams: {
                return_url:
                    window.location.href + 'orders/' + this.requestToken + '/',
            },

            loadingInfos: true,

            intervalThumbnail: 0,
            intervalUpload: 0,

            isDeviceMobile: isMobileOnly,

            modalIdTerminalConnectorChecker: 'modal-terminal-connector-checker',
            modalIdTerminalSelect: 'modal-terminal-select',
            modalIdSmartphone: 'modal-terminal-smartphone',
            modalIdTerminalAdsScreensaver: 'modal-terminal-ads-screensaver',

            terminalSources: null,
            selectedTerminalSource: null,
            terminalFilesLoading: true,
            terminalFiles: [],
            selectedTerminalFiles: [],
            terminalFilesDir: '',
            terminalFilesReplacing: false,
            terminalFilesReplacingInterval: 0,
            terminalFilesError: null,
            intervalPluginListener: 0,

            qrCode: null,

            loadingFetch: false,

            errorOrder: null,
            loadingOrder: false,
            successOrder: false,

            disableLeaveProtection: false,

            pictures: [],
            picturesError: null,

            pictureTypeErrors: [],
            pictureSizeErrors: [],
            pictureLimitErrors: [],

            triggerContactFields: 0,
            contactFields: [
                {
                    value: '',
                    valid: false,

                    name: 'name',
                    label: 'name',
                    icon: 'user',
                    rules: 'required|max:320',
                },
                {
                    value: '',
                    valid: false,

                    name: 'email',
                    label: 'emailAddress',
                    icon: 'mail',
                    rules: 'required|emailV2',
                },
                {
                    value: '',
                    valid: true,

                    name: 'telephone',
                    label: 'telephone',
                    icon: 'receiver',
                    rules: 'max:80',
                },
            ],

            triggerAddressFields: 0,
            addressFields: [
                {
                    value: '',
                    valid: false,

                    name: 'street',
                    label: 'street',
                    icon: 'home',
                    rules: 'required|max:320',
                },
                {
                    value: '',
                    valid: false,

                    name: 'zip',
                    label: 'zip',
                    icon: 'location',
                    rules: 'required|numeric|max:320',
                },
                {
                    value: '',
                    valid: false,

                    name: 'city',
                    label: 'city',
                    icon: 'world',
                    rules: 'required|max:320',
                },
            ],

            country: '',

            storeId: '',
            paymentMethod: '',
            deliveryType: '',
            requestToken: '',
            requestTokenHeaders: null,
            comments: '',

            paymentError: null,

            unifiedFormat: '',
            unifiedId: '',
            unifiedPaper: '',
            paymentRefreshTimeout: 0,

            editorId: null,
            editorRevision: 0,
            newPictureId: null,
            replacePictureId: null,
            isDraggingFiles: false,
            isPricesShown: false,

            // read by the global showTerminalHandler, which App.vue normally
            // provides together with its TerminalControlForm
            modalIdTerminalControl: 'modal-terminal-control',

            confirmTerms: false,
            triggerTerms: 0,
            termsValid: false,

            isApp: true,
            promptInstallerEvent: null,

            isTerminalConnectorModalShown: false,
        };
    },
    computed: {
        // the article every picture has, null when they differ
        sharedArticleID() {
            const ids = this.pictures.map((picture) => picture.articleID);

            return ids.length > 0 && ids.every((id) => id === ids[0])
                ? ids[0]
                : null;
        },
        articleOverview() {
            return this.getFormats.map((format) => ({
                format: format,
                papers: this.getPapersByFormat(format),
            }));
        },
        // jpeg and jpg are both accepted but name the same format
        uploadTypesText() {
            return this.getFormattedTypesString(
                this.fileExtensions
                    .filter(
                        (type) =>
                            type !== 'jpeg' ||
                            !this.fileExtensions.includes('jpg')
                    )
                    .map((type) => type.toUpperCase())
            );
        },
        editorKey() {
            if (!this.editorId) {
                return -1;
            }

            return this.pictures.findIndex(
                (picture) => picture.id === this.editorId
            );
        },
        editorPicture() {
            return this.editorKey >= 0 ? this.pictures[this.editorKey] : null;
        },
        editorIndex() {
            return this.sortedPictures.findIndex(
                (picture) => picture.id === this.editorId
            );
        },
        // a reset or a new aspect ratio remounts the cropper, which then
        // starts from the stored coordinates again
        editorCropperKey() {
            return this.editorId + '-' + this.editorRevision;
        },
        // one line per article, so graduated prices read as they are charged
        summaryRows() {
            let rows = [];
            let byArticle = {};

            const pictures = this.sortedPictures;
            for (let i = 0, j = pictures.length; i < j; i++) {
                const articleID = pictures[i].articleID;

                if (!byArticle[articleID]) {
                    byArticle[articleID] = {
                        articleID: articleID,
                        amount: 0,
                        total: 0,
                        pictures: [],
                    };
                    rows.push(byArticle[articleID]);
                }

                if (byArticle[articleID].pictures.length < 3) {
                    byArticle[articleID].pictures.push(pictures[i]);
                }

                byArticle[articleID].amount += pictures[i].amount;
                byArticle[articleID].total += this.getCostsByAmountAndArticleId(
                    pictures[i]
                );
            }

            for (let i = 0, j = rows.length; i < j; i++) {
                const article = this.getArticleByID(rows[i].articleID);

                rows[i].label =
                    this.getArticleFormat(article) +
                    ' · ' +
                    this.getPaperName(article.paper);
                rows[i].unitPrice =
                    this.getPriceByArticleID(rows[i].articleID) +
                    this.getPerUnitSuffix(rows[i]);
                rows[i].hint = null;

                const next = this.nextTierByArticle[rows[i].articleID];
                if (next) {
                    rows[i].hint = this.$t('x.at.x.amount.left.x', {
                        price:
                            this.getNumberWithCurrency(next.price) +
                            this.getPerUnitSuffix(rows[i]),
                        amount: next.amount,
                        required: next.required,
                    });
                }
            }

            return rows;
        },
        // the next graduated price per article, counted over all its pictures
        // as the price is; the tiles and the summary rows show it
        nextTierByArticle() {
            let amounts = {};
            for (let i = 0, j = this.pictures.length; i < j; i++) {
                const articleID = this.pictures[i].articleID;
                amounts[articleID] =
                    (amounts[articleID] || 0) + this.pictures[i].amount;
            }

            let next = {};
            for (const articleID in amounts) {
                const article = this.getArticleByID(articleID);
                if (!article || !article.graduatedPrices) {
                    continue;
                }

                const tier = this.getSortedGraduatedPrices(
                    article.graduatedPrices
                ).find((graduated) => graduated.amount > amounts[articleID]);
                if (tier) {
                    next[articleID] = {
                        amount: tier.amount,
                        price: tier.price,
                        required: tier.amount - amounts[articleID],
                    };
                }
            }

            return next;
        },
        getFeeDiscountText() {
            if (this.isFeeDiscount) {
                return this.$t('studio.fee.waived');
            }

            const feeDiscount = this.getFeeDiscountBySource();

            return (
                this.$t('studio.fee.free.from', {
                    x: this.getNumberWithCurrency(feeDiscount),
                }) +
                ' · ' +
                this.$t('studio.x.left', {
                    x: this.getNumberWithCurrency(
                        feeDiscount - this.getTotalSamplesPrice
                    ),
                })
            );
        },
        // what the online payment amount depends on
        orderSignature() {
            return JSON.stringify([
                this.deliveryType,
                this.pictures.map((picture) => [
                    picture.id,
                    picture.amount,
                    picture.articleID,
                    picture.printBorderID,
                    picture.measurementValue,
                    !!picture.coordinates,
                ]),
            ]);
        },
        isAddressShown() {
            return (
                this.isAddressRequired ||
                this.getInfos.mandatoryFields.includes('address')
            );
        },
        partedTerminalPaths() {
            if (this.terminalFilesDir === '') {
                return [this.$t(this.selectedTerminalSource)];
            }

            let dirs = [];
            dirs.push(this.$t(this.selectedTerminalSource));

            let terminalDirs = this.terminalFilesDir;
            let splitted = terminalDirs.split('/');

            for (let i = 0, j = splitted.length; i < j; i++) {
                if (splitted[i] === '') {
                    continue;
                }

                dirs.push(splitted[i]);
            }

            return dirs;
        },
        sortedPictures() {
            let pictures = this.pictures;

            let parsed = [];
            for (let i = 0, j = pictures.length; i < j; i++) {
                pictures[i].originKey = i;
                parsed.push(pictures[i]);
            }

            return parsed.sort(this.picturestSortFunc);
        },
        isAddressRequired() {
            if (this.paymentMethod === 'invoice') {
                return true;
            }

            if (this.deliveryType === 'shipping') {
                return true;
            }

            return false;
        },
        getDeliveryTypeOptions() {
            let options = [];
            options.push('pickup');

            if (this.getInfos.isShippingAllowed) {
                options.push('shipping');
            }

            return options;
        },
        // the delivery type is picked first, so it decides the payment methods:
        // paying in the shop is impossible for something that gets shipped
        getPaymentMethodOptions() {
            const methods = this.getPaymentMethodsSource();

            if (this.deliveryType === 'shipping') {
                return methods.filter((method) => method !== 'shop');
            }

            return methods;
        },
        getPicturesWithErrorInfo() {
            let pictures = this.pictures;

            /*  let picturesWithoutError = [];
              for (let i = 0, j = pictures.length; i < j; i++) {
                  if (pictures[i].error === null) {
                      picturesWithoutError.push(pictures[i]);
                  }
              }*/

            return pictures;
        },
        getFormats() {
            let formats = [];

            let articles = this.getInfos.articles;

            if (!articles) {
                return formats;
            }

            articles = articles.sort(this.articleFormatSortFunc);

            for (let i = 0, j = articles.length; i < j; i++) {
                let format = this.getArticleFormat(articles[i]);
                if (!formats.includes(format)) {
                    formats.push(format);
                }
            }

            return formats;
        },
        getPapers() {
            let papers = [];

            let articles = this.getInfos.articles;
            for (let i = 0, j = articles.length; i < j; i++) {
                if (!papers.includes(articles[i].paper)) {
                    papers.push(articles[i].paper);
                }
            }

            return papers;
        },
        getTotalSamplesCount() {
            let total = 0;

            let pictures = this.pictures;
            for (let i = 0, j = pictures.length; i < j; i++) {
                total += pictures[i].amount;
            }

            return total;
        },
        getTotalSamplesPrice() {
            let total = 0;

            let pictures = this.pictures;
            for (let i = 0, j = pictures.length; i < j; i++) {
                total += this.getCostsByAmountAndArticleId(pictures[i]);
            }

            return total;
        },
        getFee() {
            let total = this.getTotalSamplesPrice;
            let fee = this.getFeeBySource();
            let feeDiscount = this.getFeeDiscountBySource();

            if (feeDiscount != null && total >= feeDiscount) {
                fee = 0.0;
            }

            return fee;
        },
        getShippingCosts() {
            if (this.deliveryType !== 'shipping') {
                return 0.0;
            }

            return this.getShippingPrice();
        },
        isFeeDiscount() {
            let total = this.getTotalSamplesPrice;
            let feeDiscount = this.getFeeDiscountBySource();

            if (feeDiscount != null && total >= feeDiscount) {
                return true;
            }

            return false;
        },
        getTotal() {
            let total = this.getTotalSamplesPrice;
            let fee = this.getFee;
            let shipping = this.getShippingCosts;

            return this.getNumberWithCurrency(total + fee + shipping);
        },
        isPicturesValid() {
            if (this.successOrder || this.loadingOrder) {
                return false;
            }

            if (this.pictures.length === 0) {
                return false;
            }

            if (this.picturesError !== null) {
                return false;
            }

            for (let i = 0, j = this.pictures.length; i < j; i++) {
                if (this.isPictureTooLength(this.pictures[i])) {
                    return false;
                }
            }

            return true;
        },
        getUnifiedUploadPercentage() {
            let pictures = this.pictures;
            let total = 0;
            for (let i = 0, j = pictures.length; i < j; i++) {
                total += pictures[i].uploadPercentage;
            }

            let totalPercentage = total / pictures.length;
            return (Math.round(totalPercentage * 100) / 100).toFixed(0);
        },
        isUploadInProgress() {
            for (let i = 0, j = this.pictures.length; i < j; i++) {
                if (this.pictures[i].uploadPercentage !== 100) {
                    return true;
                }
                if (!this.pictures[i].isDone) {
                    return true;
                }
            }

            return false;
        },
        isPaymentInProgress() {
            if (this.paymentMethod !== 'online') {
                return false;
            }

            if (this.paymentKey) {
                return false;
            }

            return true;
        },
        isAnyUploadError() {
            for (let i = 0, j = this.pictures.length; i < j; i++) {
                if (this.pictures[i].error !== null) {
                    return true;
                }
            }

            return false;
        },
        isContactDetailsValid() {
            let picturesValid = this.isPicturesValid;
            if (picturesValid === false) {
                return false;
            }

            let contactFields = this.contactFields;

            for (let i = 0, j = contactFields.length; i < j; i++) {
                if (!contactFields[i].valid) {
                    return false;
                }
            }

            return true;
        },
        isStoreRequired() {
            return (
                !this.isTerminalMode &&
                this.deliveryType === 'pickup' &&
                this.getInfos.stores &&
                this.getInfos.stores.length > 0
            );
        },
    },
    watch: {
        // the order form sits on the same page as the pictures, so the payment
        // intent follows every change instead of being created once
        orderSignature() {
            clearTimeout(this.paymentRefreshTimeout);
            this.paymentRefreshTimeout = setTimeout(this.refreshPayment, 800);
        },
    },
    mounted() {
        let self = this;
        window.onunload = function () {
            if (!self.successOrder && !self.disableLeaveProtection) {
                self.deleteRequestToken();
            }
        };

        window.addEventListener('beforeinstallprompt', (e) => {
            e.preventDefault();
            self.isApp = false;
            self.promptInstallerEvent = e;
        });

        const ua = window.navigator.userAgent;
        const iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i);
        const webkit = !!ua.match(/WebKit/i);
        const iOSSafari = iOS && webkit && !ua.match(/CriOS/i);
        if (iOSSafari && !navigator.standalone) {
            this.isApp = false;
        }

        window.addEventListener('dragenter', this.dragenterListener, false);
        window.addEventListener('keydown', this.editorKeyListener, false);
        document.documentElement.classList.add('studio-page');
        document.documentElement.classList.remove('studio-boot');
    },
    beforeRouteLeave(to, from, next) {
        if (this.isStayInForm()) {
            next(false);
            window.location = to.path;
        } else {
            next();
        }
    },
    beforeDestroy() {
        window.removeEventListener('beforeunload', this.beforeWindowUnload);
        window.removeEventListener('dragenter', this.dragenterListener);
        window.removeEventListener('keydown', this.editorKeyListener);
        document.documentElement.classList.remove('studio-editor-open');
        document.documentElement.classList.remove('studio-page');
    },
    created() {
        window.addEventListener('beforeunload', this.beforeWindowUnload);

        if (this.isTerminalMode) {
            this.getTerminalSources();

            let self = this;
            setInterval(function () {
                if (self.isStart && self.isTerminalConnectorRunning !== null) {
                    if (self.isTerminalConnectorRunning) {
                        if (self.isTerminalConnectorModalShown) {
                            self.isTerminalConnectorModalShown = false;
                            UIkit.modal(
                                '#' + self.modalIdTerminalConnectorChecker
                            ).hide();
                        }

                        self.getTerminalSources();
                    } else {
                        if (!self.isTerminalConnectorModalShown) {
                            self.isTerminalConnectorModalShown = true;
                            UIkit.modal(
                                '#' + self.modalIdTerminalConnectorChecker
                            ).show();
                        }
                    }
                }
            }, 500);
        }

        if (this.isTerminalWithAds) {
            this.$store.state.start = true;
        }

        dayjs.locale(this.$i18n.locale);
        this.receiveInfosLocale();
        this.deliveryType = this.getDeliveryTypeOptions[0];
        this.paymentMethod = this.getPaymentMethodOptions[0];

        if (this.paymentMethod !== 'online') {
            this.paymentRendered = true;
        }

        this.postRequestToken();

        this.thumbnailQueue();
        this.uploadQueue();

        if (this.getInfos.articles) {
            let firstFormat = this.getFirstFormat();
            this.unifiedFormat = firstFormat;
            this.unifiedId = this.getFirstArticleIDByFormat(firstFormat);
            this.unifiedPaper = this.getArticleByID(this.unifiedId).paper;
        }
    },
    methods: {
        setPaymentRendered() {
            this.paymentRendered = true;
            // a new element starts collapsed, also with the same client secret
            this.paymentPicked = false;
            this.paymentCompleted = false;
        },
        onElementChange(event) {
            // collapsed, Stripe still reports complete for a first method
            // without fields (PayPal) and would pay with it
            this.paymentPicked = !event.collapsed;
            this.paymentCompleted = event.complete && this.paymentPicked;

            if (this.paymentPicked) {
                this.paymentPickRequired = false;
            }
        },
        getPaymentMethodsSource() {
            if (this.isTerminalMode) {
                return this.getInfos.terminal.paymentMethods;
            }

            return this.getInfos.paymentMethods;
        },
        getFeeBySource() {
            if (this.isTerminalMode) {
                return this.getInfos.terminal.fee;
            }

            return this.getInfos.fee;
        },
        getFeeDiscountBySource() {
            if (this.isTerminalMode) {
                return this.getInfos.terminal.feeDiscount;
            }

            return this.getInfos.feeDiscount;
        },
        isStayInForm() {
            let sessionInvalid =
                localStorage.getItem('session_invalid') !== null;

            return (
                this.pictures.length > 0 &&
                !this.successOrder &&
                !sessionInvalid &&
                !this.disableLeaveProtection
            );
        },
        beforeWindowUnload(e) {
            if (this.isStayInForm()) {
                // Cancel the event
                e.preventDefault();
                // Chrome requires returnValue to be set
                e.returnValue = '';
            }
        },

        getSlimDirName(dir) {
            const limit = 20;
            if (dir.length <= limit) {
                return dir;
            }

            return dir.substring(0, limit - 3) + '...';
        },
        selectDir(key) {
            if (key === 0) {
                this.terminalFilesDir = '';
            } else {
                const dirs = this.partedTerminalPaths;

                let targetDir = '';
                for (let i = 1; i < key + 1; i++) {
                    if (i > 1) {
                        targetDir += '/';
                    }
                    targetDir += dirs[i];
                }
                this.terminalFilesDir = targetDir;
            }

            this.getTerminalFiles();
        },
        handleTokenExpiry() {
            localStorage.setItem('session_invalid', 'true');
            this.reload();
        },
        screensaverStart() {
            this.postRequestToken();
        },
        getTerminalSources() {
            if (this.terminalSources && this.terminalSources.length > 0) {
                return;
            }

            let http = axios.create();
            http.get(this.apiUrlTerminal + '/sources')
                .then((response) => {
                    this.terminalSources = response.data.sources
                        ? response.data.sources
                        : [];

                    if (this.isTerminalWithAds) {
                        this.terminalSources.push('smartphone');
                        const options = {
                            text: window.location.origin,
                        };
                        this.qrCode = new QRCode(this.$refs.qrcode, options);
                    }

                    if (this.getInfos.terminal.QRCodeLink) {
                        this.terminalSources.push('smartphone');
                        const options = {
                            text: this.getInfos.terminal.QRCodeLink,
                        };
                        this.qrCode = new QRCode(this.$refs.qrcode, options);
                    }

                    this.receiveInfosLocale(response.data.token);
                })
                .catch((e) => {
                    this.checkGenericError(e);
                });
        },
        replaceTerminalFiles(files) {
            this.terminalFilesReplacing = true;

            let self = this;
            this.terminalFilesReplacingInterval = setInterval(function () {
                for (let i = 0, j = self.terminalFiles.length; i < j; i++) {
                    if (self.terminalFiles[i].thumbnailing) {
                        return;
                    }
                }

                self.terminalFiles = files;
                self.terminalFilesReplacing = false;
                self.terminalFilesLoading = false;

                clearInterval(self.terminalFilesReplacingInterval);
            }, 100);
        },
        backTerminalFolder() {
            let parts = this.terminalFilesDir.split('/');

            parts.pop();
            this.terminalFilesDir = parts.join('/');

            this.getTerminalFiles();
        },
        selectTerminalFolder(terminalPath, terminalFolder) {
            let path = terminalPath + '/' + terminalFolder;
            this.terminalFilesDir = path.substring(1);
            this.getTerminalFiles();
        },
        toggleFileSelect(key) {
            if (this.isFileSelected(key)) {
                this.deselectFile(key);
                return;
            }

            this.selectFile(key);
        },
        selectFile(key) {
            const terminalFile = this.terminalFiles[key];
            this.selectedTerminalFiles.push(terminalFile);
        },
        deselectFile(key) {
            const selectedTerminalFiles = this.selectedTerminalFiles;
            const terminalFile = this.terminalFiles[key];

            const terminalFileId =
                terminalFile.device + terminalFile.path + terminalFile.name;

            for (let i = 0, j = selectedTerminalFiles.length; i < j; i++) {
                let selectedTerminalFileId =
                    selectedTerminalFiles[i].device +
                    selectedTerminalFiles[i].path +
                    selectedTerminalFiles[i].name;

                if (terminalFileId === selectedTerminalFileId) {
                    this.selectedTerminalFiles.splice(i, 1);
                    return;
                }
            }
        },
        processSelectedPictures() {
            this.loadingFetch = true;

            const selectedTerminalFiles = this.selectedTerminalFiles;
            for (let i = 0, j = selectedTerminalFiles.length; i < j; i++) {
                let terminalFile = selectedTerminalFiles[i];
                this.fetchBlob(
                    this.apiUrlTerminal +
                        '/files/' +
                        terminalFile.device +
                        terminalFile.path +
                        '/' +
                        terminalFile.name,
                    terminalFile.name,
                    selectedTerminalFiles.length - 1 === i
                );
            }

            this.selectedTerminalFiles = [];
        },
        selectAllInFolder() {
            for (let i = 0, j = this.terminalFiles.length; i < j; i++) {
                const terminalFile = this.terminalFiles[i];

                if (terminalFile.isDir) {
                    continue;
                }

                if (this.isFileSelected(i)) {
                    continue;
                }

                this.selectedTerminalFiles.push(terminalFile);
            }
        },
        cancelSelection() {
            this.selectedTerminalFiles = [];
            UIkit.modal('#' + this.modalIdTerminalSelect).hide();
        },
        isFileSelected(key) {
            const selectedTerminalFiles = this.selectedTerminalFiles;
            const terminalFile = this.terminalFiles[key];

            const terminalFileId =
                terminalFile.device + terminalFile.path + terminalFile.name;

            for (let i = 0, j = selectedTerminalFiles.length; i < j; i++) {
                let selectedTerminalFileId =
                    selectedTerminalFiles[i].device +
                    selectedTerminalFiles[i].path +
                    selectedTerminalFiles[i].name;

                if (terminalFileId === selectedTerminalFileId) {
                    return true;
                }
            }

            return false;
        },
        selectTerminalSource(terminalSource) {
            if (terminalSource === 'smartphone') {
                UIkit.modal('#' + this.modalIdSmartphone).show();
                return;
            }

            this.selectedTerminalSource = terminalSource;
            this.terminalFilesDir = '';
            this.getTerminalFiles();

            UIkit.modal('#' + this.modalIdTerminalSelect).show();
            UIkit.tab('#sources').show(
                this.terminalSources.indexOf(terminalSource)
            );
        },
        getTerminalFiles() {
            this.terminalFilesLoading = true;

            let http = axios.create();
            http.get(
                this.apiUrlTerminal +
                    '/sources/' +
                    this.selectedTerminalSource +
                    '/' +
                    this.terminalFilesDir
            )
                .then((response) => {
                    clearInterval(this.intervalPluginListener);
                    this.intervalPluginListener = 0;
                    this.terminalFilesError = null;

                    let terminalFiles = response.data;

                    if (!terminalFiles) {
                        terminalFiles = [];
                    }

                    for (let i = 0, j = terminalFiles.length; i < j; i++) {
                        terminalFiles[i].file = null;
                        terminalFiles[i].thumbnail = null;
                        terminalFiles[i].thumbnailUrl = null;
                        terminalFiles[i].thumbnailing = false;
                    }

                    this.replaceTerminalFiles(terminalFiles);
                })
                .catch((e) => {
                    this.terminalFiles = [];
                    this.terminalFilesLoading = false;
                    this.terminalFilesError = e;
                    this.terminalFilesDir = '';

                    if (this.intervalPluginListener === 0) {
                        this.initPluginListener();
                    }
                });
        },
        initPluginListener() {
            this.intervalPluginListener = setInterval(() => {
                this.getTerminalFiles();
            }, 500);
        },
        fetchBlob(url, name, last) {
            let self = this;
            axios({
                method: 'get',
                url: url,
                responseType: 'blob',
            }).then(function (response) {
                let file = new File([response.data], name);
                self.processFilesUpload([file]);

                if (last) {
                    self.loadingFetch = false;
                    self.selectedTerminalFiles.splice(0);
                    UIkit.modal('#' + self.modalIdTerminalSelect).hide();
                }
            });
        },
        rotateCropping(key) {
            Vue.set(this.pictures[key], 'rotate', !this.pictures[key].rotate);
            this.resetCroppingByKey(key);
        },
        // the largest centered crop in the article's aspect ratio, which is
        // what the cropper itself starts with. Coordinates are in thumbnail
        // pixels, like the ones the cropper reports
        getDefaultCoordinates(key) {
            const picture = this.pictures[key];
            const imageWidth = picture.thumbnailWidth;
            const imageHeight = picture.thumbnailHeight;
            const aspectRatio = this.getCropperRatioByPictureKey(key);

            let width = imageWidth;
            let height = imageHeight;
            if (aspectRatio) {
                if (imageWidth / imageHeight > aspectRatio) {
                    width = imageHeight * aspectRatio;
                } else {
                    height = imageWidth / aspectRatio;
                }
            }

            width = Math.round(width);
            height = Math.round(height);

            return {
                width: width,
                height: height,
                left: Math.round((imageWidth - width) / 2),
                top: Math.round((imageHeight - height) / 2),
            };
        },
        resetCroppingByKey(key) {
            if (!this.pictures[key].thumbnailWidth) {
                return;
            }

            this.setPictureCoordinates(key, this.getDefaultCoordinates(key));
            this.editorRevision++;
        },
        setPictureCoordinates(key, coordinates) {
            Vue.set(this.pictures[key], 'coordinates', coordinates);
            this.getAndSetMeasurement(key);
            this.renderCroppedThumbnail(this.pictures[key]);
        },
        // only the ads screensaver still shows cropped thumbnails as images,
        // everywhere else the crop is drawn by getCropBoxStyle
        renderCroppedThumbnail(picture) {
            if (!this.isTerminalWithAds) {
                return;
            }

            const coordinates = picture.coordinates;
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = coordinates.width;
                canvas.height = coordinates.height;
                canvas
                    .getContext('2d')
                    .drawImage(
                        img,
                        coordinates.left,
                        coordinates.top,
                        coordinates.width,
                        coordinates.height,
                        0,
                        0,
                        coordinates.width,
                        coordinates.height
                    );
                canvas.toBlob(
                    (blob) => {
                        if (picture.coordinates === coordinates) {
                            Vue.set(
                                picture,
                                'thumbnailCroppedUrl',
                                this.getLocalFileUrl(blob)
                            );
                        }
                    },
                    picture.file.type,
                    1.0
                );
            };
            img.src = picture.thumbnailUrl;
        },
        // draws the crop out of the thumbnail with CSS, centered in a stage of
        // the given aspect ratio, so the grid needs no cropper per picture
        // the whole thumbnail, fitted into the tile stage
        getCropFrameStyle(item, stageRatio) {
            const ratio = item.thumbnailWidth / item.thumbnailHeight;
            const size = 88;

            let width = size;
            let height = size;
            if (ratio > stageRatio) {
                height = (size * stageRatio) / ratio;
            } else {
                width = (size * ratio) / stageRatio;
            }

            return {
                width: width + '%',
                height: height + '%',
                backgroundImage: 'url(' + item.thumbnailUrl + ')',
            };
        },
        // the crop on top of it, the rest of the picture is dimmed
        getCropBoxStyle(item) {
            const coordinates = item.coordinates;

            return {
                left: (coordinates.left / item.thumbnailWidth) * 100 + '%',
                top: (coordinates.top / item.thumbnailHeight) * 100 + '%',
                width: (coordinates.width / item.thumbnailWidth) * 100 + '%',
                height: (coordinates.height / item.thumbnailHeight) * 100 + '%',
                ...this.getPrintBorderVars(item.originKey),
            };
        },
        getPerUnitSuffix(item) {
            if (!this.getArticleByID(item.articleID).isHeightVariable) {
                return '';
            }

            return (
                ' ' +
                this.$t('per.x', {x: this.$t(this.getInfos.measurementUnit)})
            );
        },
        isLowResolution(item) {
            return (
                this.getPictureWidthLowDiff(item) > 0 ||
                this.getPictureHeightLowDiff(item) > 0
            );
        },
        isUploadTypeError(item) {
            return (
                item.error &&
                item.error.response &&
                item.error.response.status === 422
            );
        },
        getAbsenceStart(date) {
            return dayjs(date).subtract(3, 'day');
        },
        setPictureFormat(key, format) {
            const picture = this.pictures[key];
            if (picture.format === format) {
                return;
            }

            picture.format = format;
            picture.articleID = this.getFirstArticleIDByFormat(
                format,
                picture.paper
            );
            picture.paper = this.getArticleByID(picture.articleID).paper;
            picture.printBorderID = '';

            this.resetCroppingByKey(key);
        },
        setPicturePaper(key, paper) {
            const picture = this.pictures[key];
            if (picture.articleID === paper.value) {
                return;
            }

            const ratio = this.getCropperRatioByPictureKey(key);

            picture.articleID = paper.value;
            picture.paper = paper.text;
            picture.printBorderID = '';

            if (ratio !== this.getCropperRatioByPictureKey(key)) {
                this.resetCroppingByKey(key);
                return;
            }

            this.getAndSetMeasurement(key);
        },
        isOrientationSwitchable(key) {
            const article = this.getArticleByID(this.pictures[key].articleID);

            return (
                !article.isHeightVariable && article.width !== article.height
            );
        },
        setOrientation(key, orientation) {
            if (this.getPictureFormatName(key) === orientation) {
                return;
            }

            this.rotateCropping(key);
        },
        openEditor(item) {
            if (item.error || !item.thumbnailUrl) {
                return;
            }

            this.editorId = item.id;
            document.documentElement.classList.add('studio-editor-open');
        },
        closeEditor() {
            this.commitEditorCrop();
            this.editorId = null;
            document.documentElement.classList.remove('studio-editor-open');
        },
        showPrices(shown) {
            this.isPricesShown = shown;
            document.documentElement.classList.toggle('studio-editor-open', shown);
        },
        editorGo(step) {
            const pictures = this.sortedPictures;

            for (
                let i = this.editorIndex + step;
                i >= 0 && i < pictures.length;
                i += step
            ) {
                if (pictures[i].thumbnailUrl && !pictures[i].error) {
                    this.editorGoTo(pictures[i]);
                    return;
                }
            }
        },
        editorGoTo(item) {
            if (item.id === this.editorId || item.error || !item.thumbnailUrl) {
                return;
            }

            this.commitEditorCrop();
            this.editorId = item.id;
        },
        deleteEditorPicture() {
            const neighbor =
                this.sortedPictures[this.editorIndex + 1] ||
                this.sortedPictures[this.editorIndex - 1];

            this.deletePicture(this.editorKey);

            if (neighbor) {
                this.editorId = neighbor.id;
                return;
            }

            this.closeEditor();
        },
        // the cropper reports changes debounced, so a crop adjusted right
        // before switching pictures is read directly
        commitEditorCrop() {
            const cropper = this.$refs.editorCropper;
            if (!cropper || this.editorKey < 0) {
                return;
            }

            const {coordinates} = cropper.getResult();
            this.storeEditorCoordinates(coordinates);
        },
        onEditorCropperChange({coordinates, canvas}) {
            if (!this.storeEditorCoordinates(coordinates)) {
                return;
            }

            if (!this.isTerminalWithAds || !canvas) {
                return;
            }

            const picture = this.editorPicture;
            canvas.toBlob(
                (blob) => {
                    Vue.set(
                        picture,
                        'thumbnailCroppedUrl',
                        this.getLocalFileUrl(blob)
                    );
                },
                picture.file.type,
                1.0
            );
        },
        storeEditorCoordinates(coordinates) {
            const key = this.editorKey;

            if (
                key < 0 ||
                !coordinates ||
                (coordinates.height === 0 &&
                    coordinates.width === 0 &&
                    coordinates.left === 0 &&
                    coordinates.top === 0)
            ) {
                return false;
            }

            if (
                this.areCoordinatesEqual(
                    this.pictures[key].coordinates,
                    coordinates
                )
            ) {
                return false;
            }

            Vue.set(this.pictures[key], 'coordinates', coordinates);
            this.getAndSetMeasurement(key);

            return true;
        },
        editorDefaultSize() {
            const coordinates = this.editorPicture.coordinates;

            return {width: coordinates.width, height: coordinates.height};
        },
        editorDefaultPosition() {
            const coordinates = this.editorPicture.coordinates;

            return {left: coordinates.left, top: coordinates.top};
        },
        editorKeyListener(e) {
            if (this.isPricesShown && e.key === 'Escape') {
                this.showPrices(false);
                return;
            }

            if (!this.editorPicture) {
                return;
            }

            if (e.key === 'Escape') {
                this.closeEditor();
                return;
            }

            const tag = e.target && e.target.tagName;
            if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag)) {
                return;
            }

            if (e.key === 'ArrowLeft') {
                this.editorGo(-1);
            }

            if (e.key === 'ArrowRight') {
                this.editorGo(1);
            }
        },
        goToCheckout() {
            if (!this.isPicturesValid) {
                const invalid = this.sortedPictures.find((picture) =>
                    this.isPictureTooLength(picture)
                );
                if (invalid) {
                    this.scrollToSection('picture-' + invalid.id);
                }
                return;
            }

            // the watcher keeps an existing payment current; refreshing it here
            // would remount Stripe and shift the page while it scrolls
            if (!this.paymentKey) {
                this.refreshPayment();
            }
            this.scrollToSection('studio-checkout');
        },
        refreshPayment() {
            if (
                this.paymentMethod !== 'online' ||
                this.pictures.length === 0 ||
                this.pictures.some(
                    (picture) => !picture.coordinates && !picture.error
                )
            ) {
                return Promise.resolve();
            }

            return this.postOrderRequestPayment();
        },
        applyBulk() {
            for (let i = 0, j = this.pictures.length; i < j; i++) {
                const picture = this.pictures[i];

                if (
                    picture.format === this.unifiedFormat &&
                    picture.articleID === this.unifiedId
                ) {
                    continue;
                }

                picture.format = this.unifiedFormat;
                picture.articleID = this.unifiedId;
                picture.paper = this.unifiedPaper;
                picture.printBorderID = '';

                this.resetCroppingByKey(i);
            }

            UIkit.notification({
                message: this.$t('all.pictures.changed'),
                status: 'info',
                pos: 'top-center',
                timeout: this.notificationTimeout,
            });
        },
        // format and paper in one pick from the overview
        setPictureArticle(key, articleID) {
            const format = this.getArticleFormat(this.getArticleByID(articleID));

            this.setPictureFormat(key, format);
            this.setPicturePaperById(key, articleID);
        },
        setUnifiedArticle(articleID) {
            const article = this.getArticleByID(articleID);

            this.unifiedId = articleID;
            this.unifiedFormat = this.getArticleFormat(article);
            this.unifiedPaper = article.paper;
        },
        hideDropdown(event) {
            const dropdown = event.target.closest('.uk-dropdown');
            if (dropdown) {
                UIkit.dropdown(dropdown).hide(false);
            }
        },
        setPicturePaperById(key, articleID) {
            const paper = this.getPapersByFormat(
                this.pictures[key].format
            ).find((option) => option.value === articleID);
            if (paper) {
                this.setPicturePaper(key, paper);
            }
        },
        scrollToTop() {
            this.$nextTick(() => {
                this.scrollToElement(this.$refs.top);
            });
        },
        scrollToSection(id) {
            this.scrollToElement(document.getElementById(id));
        },
        // UIkit's eased scroll: the browser's smooth scroll jumps when the
        // system has animations turned off. It leaves out the sticky top bar
        // by itself, the offset is only the gap below it
        scrollToElement(element) {
            if (!element) {
                return Promise.resolve();
            }

            // an open UIkit tooltip moves along on every scroll event and puts
            // back the scroll position it saw, which undid the animation (down,
            // then back up). Close the open ones (the button just pressed) and
            // let nothing under the pointer open new ones while the page moves
            document
                .querySelectorAll('[uk-tooltip][aria-describedby]')
                .forEach((el) => UIkit.tooltip(el).hide());

            const root = document.documentElement;
            const done = () => root.classList.remove('studio-scrolling');
            root.classList.add('studio-scrolling');
            return UIkit.util
                .scrollIntoView(element, {offset: 16})
                .then(done, done);
        },
        getShippingPrice() {
            let samples = this.getTotalSamplesCount;

            let shippingGraduatedPrices = this.getSortedGraduatedPrices(
                this.getInfos.shippingGraduatedPrices,
                true
            );
            for (let i = 0, j = shippingGraduatedPrices.length; i < j; i++) {
                if (samples >= shippingGraduatedPrices[i].amount) {
                    return shippingGraduatedPrices[i].price;
                }
            }

            return 0.0;
        },
        receiveInfosLocale(token) {
            this.loadingInfos = true;

            const clickAndBuyID = this.$route.params.clickandbuy;
            let withclickandbuys = '';
            if (clickAndBuyID) {
                withclickandbuys = '?withclickandbuys=' + clickAndBuyID;
            }

            let http = axios.create();
            http.get(this.apiUrl + '/infos' + withclickandbuys, {
                headers: {
                    token: token,
                    'Content-Language': Vue.localStorage.get('language'),
                },
            })
                .then((response) => {
                    let data = response.data;
                    this.$store.state.infos = data;

                    if (data.mandatoryFields.includes('telephone')) {
                        this.contactFields[2].valid = false;
                        this.contactFields[2].rules = 'required|max:80';
                    }

                    this.country = data.shippingCountries[0];
                    if (data.shippingCountries.includes(data.country)) {
                        this.country = data.country;
                    }

                    this.loadingInfos = false;
                })
                .catch((e) => {
                    this.checkGenericError(e);
                    this.loadingInfos = false;
                });
        },
        setPaymentMethod(method) {
            if (this.successOrder || this.loadingOrder) {
                return;
            }

            this.paymentMethod = method;

            if (method === 'invoice') {
                this.country = this.getInfos.shippingCountries[0];
                if (
                    this.getInfos.shippingCountries.includes(
                        this.getInfos.country
                    )
                ) {
                    this.country = this.getInfos.country;
                }
            }

            if (method === 'online') {
                this.postOrderRequestPayment();
            } else {
                this.paymentRendered = true;
            }
        },
        setDeliveryTypes(method) {
            if (this.successOrder || this.loadingOrder) {
                return;
            }

            this.deliveryType = method;

            // shipping drops "shop" as a payment method, so move off it
            if (!this.getPaymentMethodOptions.includes(this.paymentMethod)) {
                this.setPaymentMethod(this.getPaymentMethodOptions[0]);
                return;
            }

            if (this.paymentMethod === 'online') {
                this.postOrderRequestPayment();
            }
        },
        triggerInputDialog() {
            document.getElementById('pictures').click();
        },
        triggerReplaceDialog(key) {
            this.replacePictureId = this.pictures[key].id;
            this.$refs.replacePicture.value = null;
            this.$refs.replacePicture.click();
        },
        // the drop overlay covers the whole window, so files can be dropped
        // anywhere while the pictures are shown
        dragenterListener(e) {
            if (
                this.isTerminalMode ||
                this.successOrder ||
                this.editorPicture ||
                !e.dataTransfer ||
                !Array.from(e.dataTransfer.types).includes('Files')
            ) {
                return;
            }

            this.isDraggingFiles = true;
        },
        dropListener(e) {
            this.isDraggingFiles = false;
            this.processFilesUpload(e.dataTransfer.files);
        },
        areCoordinatesEqual(oldCoordinates, newCoordinates) {
            if (!oldCoordinates) {
                return false;
            }

            if (oldCoordinates.height !== newCoordinates.height) {
                return false;
            }

            if (oldCoordinates.left !== newCoordinates.left) {
                return false;
            }

            if (oldCoordinates.top !== newCoordinates.top) {
                return false;
            }

            if (oldCoordinates.width !== newCoordinates.width) {
                return false;
            }

            return true;
        },
        getAndSetMeasurement(pictureKey) {
            let picture = this.pictures[pictureKey];

            if (!picture.coordinates) {
                return;
            }

            let article = this.getArticleByID(picture.articleID);
            let originDimensions = this.getOriginDimensions(
                picture.coordinates,
                picture.ratio
            );

            let width = originDimensions.width;
            if (width > picture.width) {
                width = picture.width;
            }

            let height = originDimensions.height;
            if (height > picture.height) {
                height = picture.height;
            }

            // the order is sent with parseInt'ed dimensions and the backend
            // recalculates the measurement from those, so round here as well
            width = parseInt(width);
            height = parseInt(height);

            let originWidth = width;
            let originHeight = height;
            if (originHeight > originWidth) {
                originWidth = height;
                originHeight = width;
            }

            const dpi = article.dpi;

            let measurementFactor = 1.0;
            if (this.getInfos.measurementUnit === 'cm') {
                measurementFactor = 2.54;
            }

            let targetPixel = (dpi / measurementFactor) * article.width;
            let newWidth = Math.ceil(
                (originWidth * targetPixel) / originHeight
            );
            let measurementValue = (newWidth * measurementFactor) / dpi;

            let measurementValueRounded =
                Math.round(measurementValue * 10) / 10;

            Vue.set(
                this.pictures[pictureKey],
                'measurementValue',
                measurementValueRounded
            );
        },
        isVariableHeight(key) {
            let item = this.pictures[key];
            let article = this.getArticleByID(item.articleID);

            return article.isHeightVariable;
        },
        getPictureFormatName(key) {
            let item = this.pictures[key];

            if (item.coordinates) {
                if (item.coordinates.height > item.coordinates.width) {
                    return 'vertical';
                }

                return 'horizontal';
            }

            if (item.height > item.width) {
                return 'vertical';
            }

            return 'horizontal';
        },
        getPrintBorders(articleID) {
            let article = this.getArticleByID(articleID);

            return article && article.printBorders ? article.printBorders : [];
        },
        // without an own title the color name and the width identify it
        getPrintBorderTitle(printBorder) {
            if (printBorder.title) {
                return printBorder.title;
            }

            let name = '';
            let suggested = this.getInfos.printBorder
                ? this.getInfos.printBorder.suggestedColors
                : [];
            for (let i = 0, j = suggested.length; i < j; i++) {
                if (suggested[i].hex === printBorder.color) {
                    name = this.$t(suggested[i].translationKey) + ' ';
                }
            }

            return name + printBorder.inMM + ' mm';
        },
        getPrintBorderByKey(key) {
            let item = this.pictures[key];
            let printBorders = this.getPrintBorders(item.articleID);

            for (let i = 0, j = printBorders.length; i < j; i++) {
                if (printBorders[i].id === item.printBorderID) {
                    return printBorders[i];
                }
            }

            return null;
        },
        getPrintBorderClass(key, withLabel) {
            if (!this.getPrintBorderByKey(key)) {
                return '';
            }

            return ' print-border' + (withLabel ? ' print-border-label' : '');
        },
        // black or white text, whichever stays readable on the border color
        getPrintBorderLabelColor(hex) {
            const value = hex.replace('#', '');
            if (value.length !== 6) {
                return '#000000';
            }

            const luminance =
                (0.299 * parseInt(value.slice(0, 2), 16) +
                    0.587 * parseInt(value.slice(2, 4), 16) +
                    0.114 * parseInt(value.slice(4, 6), 16)) /
                255;

            return luminance > 0.6 ? '#000000' : '#ffffff';
        },
        // the border is uniform in mm, so it is expressed relative to the
        // article side each stencil axis maps to
        getPrintBorderVars(key) {
            let item = this.pictures[key];
            let article = this.getArticleByID(item.articleID);
            let printBorder = this.getPrintBorderByKey(key);

            if (!article || !printBorder) {
                return {};
            }

            let shortSide = article.isHeightVariable
                ? article.width
                : Math.min(article.width, article.height);
            if (!shortSide) {
                return {};
            }

            let border = printBorder.inMM / 10;
            if (this.getInfos.measurementUnit === 'inch') {
                border = printBorder.inMM / 25.4;
            }

            let ratio = this.getCropperRatioByPictureKey(key);
            if (!ratio && item.coordinates && item.coordinates.height) {
                ratio = item.coordinates.width / item.coordinates.height;
            }
            if (!ratio) {
                ratio = 1;
            }

            let share = (border / shortSide) * 100;

            return {
                '--print-border-v': (ratio >= 1 ? share : share * ratio) + '%',
                '--print-border-h': (ratio >= 1 ? share / ratio : share) + '%',
                '--print-border-color': printBorder.color,
                '--print-border-label': JSON.stringify(
                    this.$t('frame.x.mm', {x: printBorder.inMM})
                ),
                '--print-border-label-color': this.getPrintBorderLabelColor(
                    printBorder.color
                ),
            };
        },
        getCropperRatioByPictureKey(key) {
            let item = this.pictures[key];
            let article = this.getArticleByID(item.articleID);

            if (article.isHeightVariable) {
                return null;
            }

            if (item.rotate) {
                return this.rotatedCropperRatio(article, item);
            }

            // hochformat
            if (item.height > item.width) {
                if (article.height > article.width) {
                    return article.width / article.height;
                }

                return article.height / article.width;
            }

            // querformat
            if (article.height > article.width) {
                return article.height / article.width;
            }

            return article.width / article.height;
        },
        rotatedCropperRatio(article, item) {
            // hochformat
            if (item.height > item.width) {
                if (article.height > article.width) {
                    return article.height / article.width;
                }

                return article.width / article.height;
            }

            // querformat
            if (article.height > article.width) {
                return article.width / article.height;
            }

            return article.height / article.width;
        },
        postOrderRequestPayment() {
            // this.paymentRendered = false;

            let platform = 'browser';
            if (this.isApp) {
                platform = 'app';
            }

            if (this.isTerminalMode) {
                platform = 'terminal';
            }

            let parsedPictures = [];
            for (let i = 0, j = this.pictures.length; i < j; i++) {
                if (
                    this.pictures[i].error !== null ||
                    !this.pictures[i].coordinates
                ) {
                    continue;
                }

                const thumbnailCoordinates = this.pictures[i].coordinates;
                const pictureCoordinates = this.getOriginDimensions(
                    thumbnailCoordinates,
                    this.pictures[i].ratio
                );

                let width = pictureCoordinates.width;
                let height = pictureCoordinates.height;

                if (width > this.pictures[i].width) {
                    width = this.pictures[i].width;
                }
                if (height > this.pictures[i].height) {
                    height = this.pictures[i].height;
                }

                let top = parseInt(pictureCoordinates.top);
                if (top < 0) {
                    top = 0;
                }

                let left = parseInt(pictureCoordinates.left);
                if (left < 0) {
                    left = 0;
                }

                let thumbnailTop = parseInt(thumbnailCoordinates.top);
                if (thumbnailTop < 0) {
                    thumbnailTop = 0;
                }

                let thumbnailLeft = parseInt(thumbnailCoordinates.left);
                if (thumbnailLeft < 0) {
                    thumbnailLeft = 0;
                }

                parsedPictures.push({
                    id: this.pictures[i].id,
                    amount: this.pictures[i].amount,
                    articleId: this.pictures[i].articleID,
                    printBorderId: this.pictures[i].printBorderID,
                    sort: this.pictures[i].sort,
                    measurementValue: this.pictures[i].measurementValue,

                    pictureCroppedWidth: parseInt(width),
                    pictureCroppedHeight: parseInt(height),
                    pictureCroppedTop: top,
                    pictureCroppedLeft: left,

                    thumbnailCroppedHeight: parseInt(
                        thumbnailCoordinates.height
                    ),
                    thumbnailCroppedWidth: parseInt(thumbnailCoordinates.width),
                    thumbnailCroppedTop: thumbnailTop,
                    thumbnailCroppedLeft: thumbnailLeft,
                });
            }

            let http = axios.create();
            return http
                .post(
                    this.apiUrl + '/requests/' + this.requestToken + '/payment',
                    {
                        requestId: this.requestToken,
                        deliveryType: this.deliveryType,
                        pictures: parsedPictures,
                        platform: platform,
                    }
                )
                .then((response) => {
                    if (
                        this.elementsOptions.clientSecret !== response.data.key
                    ) {
                        this.paymentCompleted = false;
                        this.paymentPicked = false;
                    }

                    this.elementsOptions.clientSecret = response.data.key;
                    this.paymentKey = true;

                    this.$refs.paymentRef.update();
                })
                .catch(() => {});
        },
        deleteOrderRequestItem(fileStruct) {
            const config = {
                headers: {
                    index: fileStruct.id,
                },
            };

            this.requestTokenHeaders = null;

            let http = axios.create();
            http.patch(
                this.apiUrl + '/requests/' + this.requestToken,
                null,
                config
            )
                .then((response) => {
                    this.requestTokenHeaders = response.headers;
                })
                .catch(() => {});
        },
        deleteRequestToken() {
            let http = axios.create();
            http.delete(this.apiUrl + '/requests/' + this.requestToken)
                .then(() => {})
                .catch(() => {});
        },
        postRequestToken() {
            this.requestTokenHeaders = null;

            let http = axios.create();
            http.post(this.apiUrl + '/requests')
                .then((response) => {
                    this.requestToken = response.data.token;
                    this.requestTokenHeaders = response.headers;
                })
                .catch((e) => {
                    this.checkGenericError(e);
                });
        },
        retryFailedPictures() {
            for (let i = 0, j = this.pictures.length; i < j; i++) {
                if (this.pictures[i].error !== null) {
                    this.pictures[i].error = null;
                    this.pictures[i].thumbnail = null;
                    this.pictures[i].thumbnailUrl = null;
                    this.pictures[i].thumbnailWidth = null;
                    this.pictures[i].thumbnailHeight = null;
                    this.pictures[i].coordinates = null;
                    this.pictures[i].thumbnailCroppedUrl = null;
                    this.pictures[i].uploadPercentage = 0;
                    this.pictures[i].isDone = false;
                    this.pictures[i].inProgress = false;
                }
            }
        },
        clonePicture(key) {
            if (this.pictures[key].cloneInProgress) {
                return;
            }

            if (this.pictures[key].uploadPercentage === 100) {
                this.cloneRequestToken(key);
                return;
            }

            const newId = uuidv4();
            this.clonePictureSuccess(
                key,
                newId,
                false,
                this.pictures[key].thumbnailUrl !== null
            );
            this.pictures[key].cloneInProgress = false;
        },
        cloneRequestToken(key) {
            const picture = this.pictures[key];
            picture.cloneInProgress = true;

            let self = this;
            this.requestTokenHeaders = null;
            const newId = uuidv4();

            let http = axios.create();
            http.put(
                this.apiUrl + '/requests/' + self.requestToken + '/clone',
                {
                    sourceId: picture.id,
                    destinationId: newId,
                }
            )
                .then((response) => {
                    // a delete meanwhile shifts the keys
                    const currentKey = self.pictures.indexOf(picture);
                    if (currentKey >= 0) {
                        self.clonePictureSuccess(currentKey, newId, true, true);
                    }
                    picture.cloneInProgress = false;

                    this.requestTokenHeaders = response.headers;
                })
                .catch((e) => {
                    picture.cloneInProgress = false;

                    self.checkGenericError(e);
                });
        },
        retryUpload(fileStruct) {
            fileStruct.error = null;
            fileStruct.thumbnail = null;
            fileStruct.thumbnailUrl = null;
            fileStruct.thumbnailWidth = null;
            fileStruct.thumbnailHeight = null;
            fileStruct.coordinates = null;
            fileStruct.thumbnailCroppedUrl = null;
            fileStruct.uploadPercentage = 0;
            fileStruct.isDone = false;
            fileStruct.inProgress = false;
        },
        putRequestToken(fileStruct) {
            let self = this;

            let formData = new FormData();
            formData.append('file', fileStruct.file, fileStruct.file.name);
            formData.append('meta', fileStruct.thumbnail, fileStruct.file.name);

            const config = {
                onUploadProgress: function (progressEvent) {
                    fileStruct.uploadPercentage = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                },
                headers: {
                    index: fileStruct.id,
                },
            };
            this.requestTokenHeaders = null;
            let http = axios.create();
            http.put(
                this.apiUrl + '/requests/' + self.requestToken,
                formData,
                config
            )
                .then((response) => {
                    fileStruct.uploadPercentage = 100;

                    fileStruct.inProgress = false;
                    fileStruct.isDone = true;

                    this.requestTokenHeaders = response.headers;
                })
                .catch((e) => {
                    self.checkGenericError(e);
                    fileStruct.uploadPercentage = 0;
                    fileStruct.error = e;
                    fileStruct.inProgress = false;
                    fileStruct.isDone = true;
                });
        },
        manipulateAmount(key, manipulator) {
            this.pictures[key].amount += manipulator;
            this.validateAmount(key);
        },
        validateAmount(key) {
            if (!this.pictures[key].amount || this.pictures[key].amount === 0) {
                this.pictures[key].amount = 1;
            }

            if (this.pictures[key].amount < 0) {
                this.pictures[key].amount *= -1;
            }
        },
        getNextTierHint(item) {
            const next = this.nextTierByArticle[item.articleID];
            if (!next) {
                return null;
            }

            return this.$t('studio.tier.next', {
                amount: next.amount,
                price:
                    this.getNumberWithCurrency(next.price) +
                    this.getPerUnitSuffix(item),
            });
        },
        getGroupedPicturesAmountByArticleID(articleID) {
            let amount = 0;

            let pictures = this.pictures;
            for (let i = 0, j = pictures.length; i < j; i++) {
                if (pictures[i].articleID === articleID) {
                    amount += pictures[i].amount;
                }
            }

            return amount;
        },
        getSortedGraduatedPrices(graduatedPrices, invert = false) {
            let graduatedPricesParsed = JSON.parse(
                JSON.stringify(graduatedPrices)
            );

            function sortNodes(a, b) {
                if (a.amount < b.amount) return -1;
                if (a.amount > b.amount) return 1;
                return 0;
            }

            function sortNodesInvert(a, b) {
                if (a.amount > b.amount) return -1;
                if (a.amount < b.amount) return 1;
                return 0;
            }

            if (invert) {
                return graduatedPricesParsed.sort(sortNodesInvert);
            }

            return graduatedPricesParsed.sort(sortNodes);
        },
        getCostsByAmountAndArticleId(item) {
            let articleID = item.articleID;
            let amount = item.amount;

            let article = this.getArticleByID(articleID);
            let price = article.price;

            if (article.graduatedPrices) {
                let graduatedPrices = this.getSortedGraduatedPrices(
                    article.graduatedPrices
                );
                let picturesAmount =
                    this.getGroupedPicturesAmountByArticleID(articleID);

                for (let k = 0, l = graduatedPrices.length; k < l; k++) {
                    if (picturesAmount >= graduatedPrices[k].amount) {
                        price = graduatedPrices[k].price;
                    }
                }
            }

            if (article.isHeightVariable) {
                let measurementValue = item.measurementValue;
                let measurementValueRounded = Math.round(measurementValue);

                price = price * measurementValueRounded;

                price = Math.round(price * 10) / 10;

                if (article.minPrice && article.minPrice > price) {
                    price = article.minPrice;
                }
            }

            return amount * price;
        },
        getPriceByArticleID(articleID) {
            let article = this.getArticleByID(articleID);

            let price = article.price;
            if (article.graduatedPrices) {
                let graduatedPrices = this.getSortedGraduatedPrices(
                    article.graduatedPrices
                );
                let picturesAmount =
                    this.getGroupedPicturesAmountByArticleID(articleID);

                for (let k = 0, l = graduatedPrices.length; k < l; k++) {
                    if (picturesAmount >= graduatedPrices[k].amount) {
                        price = graduatedPrices[k].price;
                    }
                }
            }

            return this.getNumberWithCurrency(price);
        },
        getArticleByID(articleID) {
            let articles = this.getInfos.articles;
            for (let i = 0, j = articles.length; i < j; i++) {
                if (articles[i].id === articleID) {
                    return articles[i];
                }
            }
        },
        getPictureWidthLowDiff(picture) {
            let article = this.getArticleByID(picture.articleID);
            let rec = this.getPixelFromMeasurement(article.width, article.dpi);

            if (picture.width < rec) {
                return parseInt(rec - picture.width);
            }

            return 0;
        },
        getPictureHeightLowDiff(picture) {
            let article = this.getArticleByID(picture.articleID);
            let rec = this.getPixelFromMeasurement(article.height, article.dpi);

            if (picture.height < rec) {
                return parseInt(rec - picture.height);
            }

            return 0;
        },
        getPapersByFormat(format) {
            let papers = [];

            let articles = this.getInfos.articles;
            for (let i = 0, j = articles.length; i < j; i++) {
                let rowFormat = this.getArticleFormat(articles[i]);

                if (format && format !== rowFormat) {
                    continue;
                }

                let perUnit = '';
                if (articles[i].isHeightVariable) {
                    perUnit =
                        ' ' +
                        this.$t('per.x', {
                            x: this.$t(this.getInfos.measurementUnit),
                        });
                }

                // the single price, and the top graduated price as one line
                // under it ("ab 50 Stk. 0,30 €")
                let price = articles[i].price;
                let tierHint = null;
                if (articles[i].graduatedPrices) {
                    const graduatedPrices = this.getSortedGraduatedPrices(
                        articles[i].graduatedPrices
                    );
                    let top = null;
                    for (let k = 0, l = graduatedPrices.length; k < l; k++) {
                        // a tier at 1 replaces the base price
                        if (graduatedPrices[k].amount <= 1) {
                            price = graduatedPrices[k].price;
                        } else {
                            top = graduatedPrices[k];
                        }
                    }

                    if (top !== null && top.price < price) {
                        tierHint = this.$t('studio.tier.next', {
                            amount: top.amount,
                            price:
                                this.getNumberWithCurrency(top.price) + perUnit,
                        });
                    }
                }

                let priceString = this.getNumberWithCurrency(price) + perUnit;

                let minPriceString = null;
                if (articles[i].isHeightVariable && articles[i].minPrice) {
                    minPriceString = this.getNumberWithCurrency(
                        articles[i].minPrice
                    );
                }

                papers.push({
                    value: articles[i].id,
                    text: articles[i].paper,
                    price: priceString,
                    tierHint: tierHint,
                    minPrice: minPriceString,
                });
            }

            papers = papers.sort((a, b) =>
                this.comparePaperNames(a.text, b.text)
            );

            return papers;
        },
        getFormatsByPaper(paper) {
            let formats = [];

            let articles = this.getInfos.articles;
            for (let i = 0, j = articles.length; i < j; i++) {
                if (paper && articles[i].paper !== paper) {
                    continue;
                }

                formats.push({
                    value: articles[i].id,
                    width: articles[i].width,
                    height: articles[i].height,
                    text:
                        this.getArticleFormat(articles[i]) +
                        ' (' +
                        this.getNumberWithCurrency(articles[i].price) +
                        ')',
                });
            }

            return formats.sort(this.articleFormatSortFunc);
        },
        replacePicture() {
            const file = this.$refs.replacePicture.files[0];
            const i = this.pictures.findIndex(
                (picture) => picture.id === this.replacePictureId
            );
            if (!file || i < 0) {
                return;
            }

            this.pictures[i].file = file;
            this.pictures[i].thumbnail = null;
            this.pictures[i].thumbnailUrl = null;
            this.pictures[i].thumbnailWidth = null;
            this.pictures[i].thumbnailHeight = null;
            this.pictures[i].coordinates = null;
            this.pictures[i].thumbnailCroppedUrl = null;
            this.pictures[i].uploadPercentage = 0;
            this.pictures[i].error = null;
            this.pictures[i].isDone = false;
            this.pictures[i].inProgress = false;
        },
        deletePicture(key) {
            this.deleteOrderRequestItem(this.pictures[key]);
            this.pictures.splice(key, 1);
        },
        clonePictureSuccess(key, newId, copyStatus, copyMeta) {
            let picture = this.pictures[key];

            let uploadPercentage = 0;
            let error = null;
            let isDone = false;
            let inProgress = false;

            if (copyStatus) {
                uploadPercentage = picture.uploadPercentage;
                error = picture.error;
                isDone = picture.isDone;
                inProgress = picture.inProgress;
            }

            let thumbnail = null;
            let thumbnailUrl = null;
            let thumbnailWidth = null;
            let thumbnailHeight = null;
            let coordinates = null;
            let width = null;
            let height = null;
            let thumbnailCroppedUrl = null;
            let ratio = null;

            if (copyMeta) {
                thumbnail = picture.thumbnail;
                thumbnailUrl = picture.thumbnailUrl;
                thumbnailWidth = picture.thumbnailWidth;
                thumbnailHeight = picture.thumbnailHeight;
                coordinates = picture.coordinates;
                width = picture.width;
                height = picture.height;
                thumbnailCroppedUrl = picture.thumbnailCroppedUrl;
                ratio = picture.ratio;
            }

            // the copy goes right after its original: renumber the order, so
            // repeated copies never run out of room between two sort values
            const sorted = this.sortedPictures;
            const index = sorted.indexOf(picture);
            for (let i = 0, j = sorted.length; i < j; i++) {
                sorted[i].sort = i <= index ? i : i + 1;
            }
            const sortVal = index + 1;

            // the copy keeps format and crop, it is mostly made to order the
            // same picture a second way
            let newFileStruct = {
                sort: sortVal,

                id: newId,
                format: picture.format,
                paper: picture.paper,
                amount: 1,
                articleID: picture.articleID,
                printBorderID: picture.printBorderID,
                rotate: picture.rotate,

                measurementValue: picture.measurementValue,

                file: picture.file,

                thumbnail: thumbnail,
                thumbnailUrl: thumbnailUrl,
                thumbnailWidth: thumbnailWidth,
                thumbnailHeight: thumbnailHeight,
                coordinates: coordinates,
                width: width,
                height: height,
                thumbnailCroppedUrl: thumbnailCroppedUrl,
                ratio: ratio,

                uploadPercentage: uploadPercentage,
                error: error,
                isDone: isDone,
                inProgress: inProgress,
            };

            this.pictures.push(newFileStruct);

            if (this.editorPicture) {
                return;
            }

            // it sits next to the original: scroll to it only when it is cut
            // off, and ring it briefly, since it is the same photo
            this.newPictureId = newId;
            this.$nextTick(() => {
                const element = document.getElementById('picture-' + newId);
                const rect = element ? element.getBoundingClientRect() : null;
                const done = () => {
                    setTimeout(() => {
                        if (this.newPictureId === newId) {
                            this.newPictureId = null;
                        }
                    }, 1200);
                };

                if (
                    rect &&
                    (rect.top < this.$refs.nav.offsetHeight ||
                        rect.bottom > window.innerHeight)
                ) {
                    this.scrollToElement(element).then(done);
                    return;
                }

                done();
            });
        },
        handleFilesUpload() {
            let files = this.$refs['pictures'].files;

            /*
            if (files.length > this.getInfos.maxPicturesPerOrder) {
                this.picturesError = this.$t('pictures.max.reached.drop', {x: this.getInfos.maxPicturesPerOrder});
                return;
            }
             */

            this.processFilesUpload(files);
        },
        getFirstFormat() {
            let formats = this.getFormats;

            let favoriteArticle = this.getFavoriteArticle();
            if (favoriteArticle) {
                return this.getArticleFormat(favoriteArticle);
            }

            return formats[0];
        },
        getFavoriteArticle() {
            let articles = this.getInfos.articles;

            if (articles) {
                for (let i = 0, j = articles.length; i < j; i++) {
                    if (articles[i].isFavorite) {
                        return articles[i];
                    }
                }
            }

            return null;
        },
        getFirstArticleIDByFormat(format, paper) {
            if (!format) {
                if (!this.getInfos.articles) {
                    return 0;
                }

                let favoriteArticle = this.getFavoriteArticle();
                if (favoriteArticle) {
                    return favoriteArticle.id;
                }

                return this.getInfos.articles[0].id;
            }

            let articles = this.getInfos.articles;

            articles = articles.sort(
                (a, b) => Number(b.isFavorite) - Number(a.isFavorite)
            );

            articles = articles.sort(this.articleFormatSortFunc);

            if (paper) {
                paper = this.getPaperName(paper);

                for (let i = 0, j = articles.length; i < j; i++) {
                    let parsedFormat = this.getArticleFormat(articles[i]);
                    if (
                        format === parsedFormat &&
                        paper === this.getPaperName(articles[i].paper)
                    ) {
                        return articles[i].id;
                    }
                }
            }

            for (let i = 0, j = articles.length; i < j; i++) {
                let parsedFormat = this.getArticleFormat(articles[i]);
                if (format === parsedFormat) {
                    return articles[i].id;
                }
            }
        },
        getArticleFormat(article) {
            if (article.name) {
                return article.name;
            }

            if (article.isHeightVariable) {
                return (
                    this.roundWidth(article.width) +
                    ' ' +
                    this.$t(this.getInfos.measurementUnit) +
                    ' x ' +
                    this.$t('pictureLength')
                );
            }

            return (
                this.roundWidth(article.width) +
                ' x ' +
                this.roundHeight(article.height) +
                ' ' +
                this.$t(this.getInfos.measurementUnit)
            );
        },
        articleFormatSortFunc(a, b) {
            if (a.width < b.width) return -1;
            if (a.width > b.width) return 1;
            return 0;
        },
        picturestSortFunc(a, b) {
            if (a.sort < b.sort) return -1;
            if (a.sort > b.sort) return 1;
            return 0;
        },
        getFirstArticleIDByPaper(paper) {
            if (!paper) {
                return this.getInfos.articles[0].id;
            }

            let articles = this.getInfos.articles;

            articles = articles.sort(this.articleFormatSortFunc);

            for (let i = 0, j = articles.length; i < j; i++) {
                if (articles[i].paper === paper) {
                    return articles[i].id;
                }
            }
        },
        processFileEntries(files) {
            let self = this;

            let firstFormat = this.getFirstFormat();
            let firstArticleID = this.getFirstArticleIDByFormat(firstFormat);
            let firstPaper = this.getArticleByID(firstArticleID).paper;

            // after the highest sort: a copy is the last in the list, but sorted
            // next to its original
            let sortModifier = 0;
            for (let i = 0, j = self.pictures.length; i < j; i++) {
                sortModifier = Math.max(sortModifier, self.pictures[i].sort + 1);
            }

            for (let i = 0, j = files.length; i < j; i++) {
                if (self.pictures.length >= this.getInfos.maxPicturesPerOrder) {
                    this.pictureLimitErrors.push(files[i].name);
                    continue;
                }

                let newFileStruct = {
                    sort: sortModifier + i,

                    id: uuidv4(),
                    format: firstFormat,
                    paper: firstPaper,
                    amount: 1,
                    articleID: firstArticleID,
                    printBorderID: '',
                    measurementValue: 0.0,

                    file: files[i],
                    thumbnail: null,

                    thumbnailUrl: null,
                    thumbnailWidth: null,
                    thumbnailHeight: null,
                    coordinates: null,
                    thumbnailCroppedUrl: null,
                    ratio: null,

                    uploadPercentage: 0,
                    error: null,
                    isDone: false,
                    inProgress: false,
                };

                self.pictures.push(newFileStruct);
            }
        },
        setPictureError() {
            if (this.pictureTypeErrors.length > 0) {
                this.picturesError = this.$t('files.type.error', {
                    name: this.$t('pictures'),
                    types: this.uploadTypesText,
                });
                return;
            }

            if (this.pictureSizeErrors.length > 0) {
                this.picturesError = this.$t('files.size.error', {
                    name: this.$t('pictures'),
                    mb: this.getMaxFileSizeInMB,
                });
                return;
            }

            if (this.pictureLimitErrors.length > 0) {
                this.picturesError = this.$t('pictures.max.reached.drop', {
                    x: this.getInfos.maxPicturesPerOrder,
                });
            }
        },
        showPictureErrors() {
            // this.setPictureError();
            const messageTimeout = 0;

            if (this.pictureTypeErrors.length > 0) {
                UIkit.notification({
                    message:
                        this.$t('files.type.error', {
                            name: this.$t('pictures'),
                            types: this.uploadTypesText,
                        }) + '.',
                    status: 'danger',
                    pos: 'top-center',
                    timeout: messageTimeout,
                });

                this.pictureTypeErrors = [];
            }

            if (this.pictureSizeErrors.length > 0) {
                UIkit.notification({
                    message:
                        this.$t('files.size.error', {
                            name: this.$t('pictures'),
                            mb: this.getMaxFileSizeInMB,
                        }) + '.',
                    status: 'danger',
                    pos: 'top-center',
                    timeout: messageTimeout,
                });

                this.pictureSizeErrors = [];
            }

            if (this.pictureLimitErrors.length > 0) {
                UIkit.notification({
                    message:
                        this.$t('pictures.max.reached.drop', {
                            x: this.getInfos.maxPicturesPerOrder,
                        }) + '.',
                    status: 'danger',
                    pos: 'top-center',
                    timeout: messageTimeout,
                });

                this.pictureLimitErrors = [];
            }
        },
        processFilesUpload(files) {
            this.picturesError = null;
            let parsedFiles = [];

            for (let i = 0, j = files.length; i < j; i++) {
                let fileExtension = files[i].name.split('.').pop();
                fileExtension = fileExtension.toLowerCase();
                if (!this.fileExtensions.includes(fileExtension)) {
                    this.pictureTypeErrors.push(files[i].name);
                    continue;

                    /*
                    this.picturesError = this.$t("files.type.error", {
                        name: this.$t('pictures'),
                        types: this.getFormattedTypesString(this.fileExtensions)
                    });

                    return;
                     */
                }

                if (files[i].size > this.getMaxFileSizeInMB * 1024 * 1024) {
                    this.pictureSizeErrors.push(files[i].name);
                    continue;

                    /*
                    this.picturesError = this.$t("files.size.error", {
                        name: this.$t('pictures'),
                        mb: this.getMaxFileSizeInMB
                    });
                    return;
                */
                }

                parsedFiles.push(files[i]);
            }

            if (parsedFiles.length === 0) {
                this.showPictureErrors();
            }

            /*
            let checkedFiles = [];
            for (let i = 0, j = parsedFiles.length; i < j; i++) {
                let self = this;

                try {
                let fileReader = new FileReader();
                fileReader.onloadend = function (e) {
                    let arr = (new Uint8Array(e.target.result)).subarray(0, 4);
                    let header = '';
                    for (let i = 0; i < arr.length; i++) {
                        header += arr[i].toString(16);
                    }

                    let type = 'unknown';
                    switch (header) {
                        case '89504e47':
                            type = 'image/png';
                            break;
                        case "ffd8ffe0":
                        case "ffd8ffe1":
                        case "ffd8ffe2":
                        case "ffd8ffe3":
                        case "ffd8ffe8":
                        case "ffd8ffdb":
                            type = 'image/jpeg';
                            break;
                    }

                    if (type === 'unknown') {

                        self.pictureTypeErrors.push(parsedFiles[i].name);


                        //self.picturesError = self.$t("files.type.error", {
                        //    name: self.$t('pictures'),
                      //      types: self.getFormattedTypesString(self.fileExtensions)
                        //});
                        //return;

                    } else {
                        checkedFiles.push(parsedFiles[i]);
                    }


                   // if (self.picturesError === null && i === (files.length - 1)) {
                     //   self.processFileEntries(files);
                    //}


                    if (i === (parsedFiles.length - 1)) {
                        self.processFileEntries(checkedFiles);
                        self.showPictureErrors();
                    }

                };
                fileReader.readAsArrayBuffer(parsedFiles[i]);

                } catch (e) {
                    self.pictureTypeErrors.push(parsedFiles[i].name);

                }

            }
            // onload
            */

            this.processFileEntries(parsedFiles);
            this.showPictureErrors();
        },
        thumbnailQueue() {
            let self = this;
            this.intervalThumbnail = setInterval(() => {
                for (let i = 0, j = self.pictures.length; i < j; i++) {
                    if (self.pictures[i].thumbnailing) {
                        return;
                    }
                }

                for (let i = 0, j = self.pictures.length; i < j; i++) {
                    if (self.pictures[i].thumbnailUrl) {
                        continue;
                    }

                    self.pictures[i].thumbnailing = true;
                    self.imageScaleDown(i);

                    return;
                }
            }, 100);
        },
        thumbnailQueueTerminall() {
            let self = this;
            setInterval(() => {
                if (!self.isTerminalMode) {
                    return;
                }

                if (!self.terminalFiles) {
                    return;
                }

                if (self.terminalFilesReplacing) {
                    return;
                }

                if (self.terminalFiles.length === 0) {
                    return;
                }

                for (let i = 0, j = self.terminalFiles.length; i < j; i++) {
                    if (self.terminalFiles[i].thumbnailing) {
                        return;
                    }
                }

                for (let i = 0, j = self.terminalFiles.length; i < j; i++) {
                    if (self.terminalFiles[i].thumbnailUrl) {
                        continue;
                    }

                    if (self.terminalFiles[i].isDir) {
                        continue;
                    }

                    self.terminalFiles[i].thumbnailing = true;
                    self.fetchFileTerminal(i);

                    return;
                }
            }, 100);
        },
        uploadQueue() {
            let self = this;
            this.intervalUpload = setInterval(() => {
                for (let i = 0, j = self.pictures.length; i < j; i++) {
                    if (self.pictures[i].inProgress) {
                        return;
                    }
                }

                for (let i = 0, j = self.pictures.length; i < j; i++) {
                    if (self.pictures[i].isDone) {
                        continue;
                    }

                    if (!self.pictures[i].file) {
                        continue;
                    }

                    if (!self.pictures[i].thumbnail) {
                        continue;
                    }

                    self.pictures[i].inProgress = true;
                    self.putRequestToken(self.pictures[i]);

                    return;
                }
            }, 100);
        },

        fetchFileTerminal(key) {
            const file = this.terminalFiles[key];

            let self = this;
            axios({
                method: 'get',
                url:
                    self.apiUrlTerminal +
                    '/files/' +
                    file.device +
                    file.path +
                    '/' +
                    file.name,
                responseType: 'blob',
            }).then(function (response) {
                let object = new File([response.data], file.name);
                Vue.set(self.terminalFiles[key], 'file', object);
                self.imageScaleDownTerminal(key);
            });
        },
        imageScaleDownTerminal(key) {
            const file = this.terminalFiles[key].file;
            let self = this;

            const blobURL = URL.createObjectURL(file);
            const img = new Image();
            img.src = blobURL;
            img.onerror = function () {
                URL.revokeObjectURL(this.src);
                Vue.set(self.terminalFiles[key], 'error', 'cannot load image');
            };
            img.onload = function () {
                URL.revokeObjectURL(this.src);
                const [newWidth, newHeight] = self.calculateAspectRatioFit(
                    img.width,
                    img.height,
                    436,
                    638
                );

                const canvas = document.createElement('canvas');
                canvas.width = newWidth;
                canvas.height = newHeight;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, newWidth, newHeight);
                canvas.toBlob(
                    (blob) => {
                        Vue.set(self.terminalFiles[key], 'thumbnail', blob);
                        Vue.set(
                            self.terminalFiles[key],
                            'thumbnailUrl',
                            self.getLocalFileUrl(blob)
                        );
                        Vue.set(self.terminalFiles[key], 'thumbnailing', false);
                    },
                    file.type,
                    0.5
                );
            };
        },
        imageScaleDown(key) {
            // held by reference, the index shifts when a picture is deleted
            // while this one is still being scaled
            const picture = this.pictures[key];
            const file = picture.file;
            let self = this;

            const blobURL = URL.createObjectURL(file);
            const img = new Image();
            img.src = blobURL;
            img.onerror = function () {
                URL.revokeObjectURL(this.src);
                Vue.set(picture, 'error', 'cannot load image');
            };
            img.onload = function () {
                URL.revokeObjectURL(this.src);

                Vue.set(picture, 'width', img.width);
                Vue.set(picture, 'height', img.height);
                const [newWidth, newHeight, ratio] =
                    self.calculateAspectRatioFit(
                        img.width,
                        img.height,
                        436,
                        638
                    );
                Vue.set(picture, 'ratio', ratio);

                const canvas = document.createElement('canvas');
                canvas.width = newWidth;
                canvas.height = newHeight;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, newWidth, newHeight);
                canvas.toBlob(
                    (blob) => {
                        Vue.set(picture, 'thumbnail', blob);
                        Vue.set(picture, 'thumbnailWidth', canvas.width);
                        Vue.set(picture, 'thumbnailHeight', canvas.height);
                        Vue.set(
                            picture,
                            'thumbnailUrl',
                            self.getLocalFileUrl(blob)
                        );
                        Vue.set(picture, 'thumbnailing', false);

                        const currentKey = self.pictures.indexOf(picture);
                        if (currentKey >= 0 && !picture.coordinates) {
                            self.setPictureCoordinates(
                                currentKey,
                                self.getDefaultCoordinates(currentKey)
                            );
                        }
                    },
                    file.type,
                    0.5
                );
            };
        },
        getMimeType(file, fallback = null) {
            const byteArray = new Uint8Array(file).subarray(0, 4);
            let header = '';
            for (let i = 0; i < byteArray.length; i++) {
                header += byteArray[i].toString(16);
            }
            switch (header) {
                case '89504e47':
                    return 'image/png';
                case '47494638':
                    return 'image/gif';
                case 'ffd8ffe0':
                case 'ffd8ffe1':
                case 'ffd8ffe2':
                case 'ffd8ffe3':
                case 'ffd8ffe8':
                    return 'image/jpeg';
                default:
                    return fallback;
            }
        },
        calculateSize(img, maxWidth, maxHeight) {
            let width = img.width;
            let height = img.height;

            if (width > height) {
                if (width > maxWidth) {
                    height = Math.round((height * maxWidth) / width);
                    width = maxWidth;
                }
            } else {
                if (height > maxHeight) {
                    width = Math.round((width * maxHeight) / height);
                    height = maxHeight;
                }
            }

            return [width, height];
        },
        setValidContactFields(valid, fieldKey) {
            this.contactFields[fieldKey].valid = valid;
        },
        setValidAddressFields(valid, fieldKey) {
            this.addressFields[fieldKey].valid = valid;
        },
        setTermsValid(valid) {
            this.termsValid = valid;
        },
        async complete() {
            if (this.loadingOrder) {
                return;
            }

            this.triggerContactFields++;
            this.triggerTerms++;

            if (!this.isPicturesValid) {
                const invalid = this.sortedPictures.find((picture) =>
                    this.isPictureTooLength(picture)
                );
                if (invalid) {
                    this.scrollToSection('picture-' + invalid.id);
                }
                return;
            }

            if (!this.isContactDetailsValid) {
                this.scrollToSection('checkout-contact');
                return;
            }

            if (this.isStoreRequired && !this.storeId) {
                this.scrollToSection('checkout-delivery');
                return;
            }

            let street = null;
            let zip = null;
            let city = null;
            let country = null;
            if (this.isAddressShown) {
                this.triggerAddressFields++;

                for (let i = 0, j = this.addressFields.length; i < j; i++) {
                    if (!this.addressFields[i].valid) {
                        this.scrollToSection('checkout-delivery');
                        return;
                    }
                }

                street = this.addressFields[0].value;
                zip = this.addressFields[1].value;
                city = this.addressFields[2].value;
                country = this.country;
            }

            if (!this.termsValid) {
                this.scrollToSection('checkout-terms');
                return;
            }

            if (this.paymentMethod === 'online' && !this.paymentCompleted) {
                this.scrollToSection('payment-method');
                // focusing the collapsed list would open its first method
                if (this.paymentPicked) {
                    this.$refs.paymentRef.focus();
                } else {
                    this.paymentPickRequired = true;
                }
                return;
            }

            // the amount must match the order as it is now, not as it was when
            // the payment form appeared
            if (this.paymentMethod === 'online') {
                clearTimeout(this.paymentRefreshTimeout);
                await this.postOrderRequestPayment();

                if (!this.paymentCompleted) {
                    this.paymentPickRequired = true;
                    this.scrollToSection('payment-method');
                    return;
                }
            }

            this.loadingOrder = true;

            let parsedPictures = [];
            for (let i = 0, j = this.pictures.length; i < j; i++) {
                if (this.pictures[i].error !== null) {
                    continue;
                }

                const thumbnailCoordinates = this.pictures[i].coordinates;
                const pictureCoordinates = this.getOriginDimensions(
                    thumbnailCoordinates,
                    this.pictures[i].ratio
                );

                let width = pictureCoordinates.width;
                let height = pictureCoordinates.height;

                if (width > this.pictures[i].width) {
                    width = this.pictures[i].width;
                }
                if (height > this.pictures[i].height) {
                    height = this.pictures[i].height;
                }

                let top = parseInt(pictureCoordinates.top);
                if (top < 0) {
                    top = 0;
                }

                let left = parseInt(pictureCoordinates.left);
                if (left < 0) {
                    left = 0;
                }

                let thumbnailTop = parseInt(thumbnailCoordinates.top);
                if (thumbnailTop < 0) {
                    thumbnailTop = 0;
                }

                let thumbnailLeft = parseInt(thumbnailCoordinates.left);
                if (thumbnailLeft < 0) {
                    thumbnailLeft = 0;
                }

                parsedPictures.push({
                    id: this.pictures[i].id,
                    amount: this.pictures[i].amount,
                    articleId: this.pictures[i].articleID,
                    printBorderId: this.pictures[i].printBorderID,
                    sort: this.pictures[i].sort,
                    measurementValue: this.pictures[i].measurementValue,

                    pictureCroppedWidth: parseInt(width),
                    pictureCroppedHeight: parseInt(height),
                    pictureCroppedTop: top,
                    pictureCroppedLeft: left,

                    thumbnailCroppedHeight: parseInt(
                        thumbnailCoordinates.height
                    ),
                    thumbnailCroppedWidth: parseInt(thumbnailCoordinates.width),
                    thumbnailCroppedTop: thumbnailTop,
                    thumbnailCroppedLeft: thumbnailLeft,
                });
            }

            let platform = 'browser';
            if (this.isApp) {
                platform = 'app';
            }

            if (this.isTerminalMode) {
                platform = 'terminal';
            }

            let http = axios.create();
            http.post(this.apiUrl + '/orders', {
                requestId: this.requestToken,
                name: this.contactFields[0].value,
                email: this.contactFields[1].value,
                telephone: this.contactFields[2].value,
                storeId: this.deliveryType === 'pickup' ? this.storeId : '',
                paymentMethod: this.paymentMethod,
                deliveryType: this.deliveryType,
                comments: this.comments,
                pictures: parsedPictures,
                language: this.$i18n.locale,
                platform: platform,

                street,
                zip,
                city,
                country,
            })
                .then(async () => {
                    let self = this;

                    if (this.paymentMethod === 'online') {
                        this.disableLeaveProtection = true;

                        try {
                            const result =
                                await this.$refs.paymentRef.stripe.confirmPayment(
                                    {
                                        elements:
                                            this.$refs.paymentRef.elements,
                                        confirmParams: {
                                            return_url:
                                                window.location.href.endsWith(
                                                    '/'
                                                )
                                                    ? window.location.href +
                                                      'orders/' +
                                                      this.requestToken +
                                                      '/'
                                                    : window.location.href +
                                                      '/orders/' +
                                                      this.requestToken +
                                                      '/',
                                        },
                                        // redirect: 'if_required',
                                    }
                                );

                            if (result.error) {
                                self.loadingOrder = false;
                                // UIkit.scroll('#payment-method', {offset: 0}).scrollTo('#payment-method');
                                this.$refs.paymentRef.focus();
                            } else {
                                self.loadingOrder = false;

                                self.errorOrder = null;
                            }
                        } catch (e) {
                            self.loadingOrder = false;
                            this.$refs.paymentRef.focus();
                            this.paymentError = e;
                        }
                    } else {
                        this.errorOrder = null;
                        this.successOrder = true;
                        this.loadingOrder = false;

                        if (this.isTerminalWithAds) {
                            let self = this;
                            setTimeout(function () {
                                UIkit.modal(
                                    '#' + self.modalIdTerminalAdsScreensaver
                                ).show();
                            }, 10);
                            return;
                        }

                        this.scrollToTop();
                    }
                })
                .catch((e) => {
                    if (e.response && e.response.status === 409) {
                        this.errorOrder = null;
                        this.loadingOrder = false;
                        this.successOrder = true;
                        this.scrollToTop();
                        return;
                    }

                    this.checkGenericError(e);
                    this.errorOrder = e;
                    this.loadingOrder = false;
                    this.successOrder = false;

                    if (
                        this.paymentMethod === 'online' &&
                        (!e.response ||
                            !e.response.data ||
                            !e.response.data.error)
                    ) {
                        this.postOrderRequestPayment();
                    }
                });
        },
    },
};
</script>

<style lang="scss" scoped>
// LunaCart family (white round cards, the generic components) in a slim
// single page: top bar, pictures, order, legal footer
$page: #f5f4f1;
$line: #e8e8e6;
$muted: #707070;
$nav: 64px;

.studio {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    background: $page;
    color: $site-background;
}

.studio-container {
    box-sizing: border-box;
    width: 100%;
    max-width: 1240px;
    margin: 0 auto;
    padding: 0 24px;
}

@media (max-width: $breakpoint-xsmall-max) {
    .studio-container {
        padding: 0 14px;
    }
}

.studio-muted {
    color: $muted;
}

.studio-small {
    font-size: 13px;
}

.studio-strong {
    font-weight: 500;
}

.studio-danger {
    color: $danger-color;
}

.studio-accent {
    color: $info-color;
}

.studio-link {
    color: inherit;
    text-decoration: underline !important;
    cursor: pointer;
}

.studio-title {
    margin: 0;
    color: $site-background;
    font-size: 22px;
    font-weight: 500;
    line-height: 1.3;

    .studio-muted {
        font-weight: 400;
    }
}

.studio-card-title {
    margin-bottom: 12px;
    color: $site-background;
    font-size: 17px;
    font-weight: 500;
}

// top bar

.studio-nav {
    position: sticky;
    top: 0;
    z-index: 980;
    background: rgba(255, 255, 255, 0.92);
    backdrop-filter: saturate(180%) blur(12px);
    border-bottom: 1px solid $line;
}

.studio-nav-inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    height: $nav;
}

.studio-brand {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
}

.studio-logo {
    max-height: 36px;
    max-width: 140px;
    border-radius: $border-radius-next;
    object-fit: contain;
}

.studio-name {
    overflow: hidden;
    font-size: 17px;
    font-weight: 500;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.studio-nav-end {
    display: flex;
    flex: none;
    align-items: center;
    gap: 14px;
}

.studio-nav-total {
    white-space: nowrap;
}

.studio-nav-progress {
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    width: 100%;
    height: 3px !important;
    margin: 0 !important;
    border-radius: 0 !important;
}

@media (max-width: $breakpoint-xsmall-max) {
    .studio-nav-inner {
        height: 56px;
    }

    .studio-logo {
        max-height: 30px;
        max-width: 90px;
    }

    .studio-name {
        font-size: 15px;
    }
}

// page

.studio-main {
    flex: 1;
    padding-top: 32px;
    padding-bottom: 40px;
}

.studio-notice {
    margin-bottom: 20px;
    padding: 12px 16px;
    border-radius: 16px;
    background: #fff4d6;
    color: #5c4400;
    text-align: center;
}

.studio-section {
    scroll-margin-top: $nav + 16px;

    & + .studio-section {
        margin-top: 48px;
    }
}

.studio-section-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;

    // the action right after the title, in line with "Für alle Bilder" below
    &.is-start {
        justify-content: flex-start;
    }
}

// empty state

.studio-dropzone {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 56px 20px;
    border: 2px dashed #d4d4d0;
    border-radius: 16px;
    background: #fff;
    text-align: center;
    cursor: pointer;
    transition: border-color 0.2s;

    &:hover {
        border-color: $info-color;
    }
}

.studio-dropzone-title {
    margin-top: 14px;
    font-size: 20px;
    font-weight: 500;
}

// drag and drop only exists with a mouse
.studio-if-touch {
    display: none;
}

@media (hover: none) {
    .studio-if-pointer {
        display: none;
    }

    .studio-if-touch {
        display: inline;
    }
}

.studio-prices-head {
    margin-top: 20px;
    text-align: center;
}

// in a modal, so the drop zone stays the one thing on the empty page
.studio-prices {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 12px;
    min-height: 0;
    padding: 0 20px 20px 20px;
    overflow-y: auto;
}

.studio-price {
    padding: 16px 18px;
    border-radius: 16px;
    background: #f5f4f1;
}

.studio-price-format {
    margin-bottom: 8px;
    font-size: 16px;
    font-weight: 500;
}

.studio-price-row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 5px 0;
    border-top: 1px solid $line;
    color: $muted;
    font-size: 14px;

    .studio-strong {
        color: $site-background;
        white-space: nowrap;
    }
}

.studio-price .studio-overview-tiers {
    padding-bottom: 6px;
}

.studio-add-button {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 38px;
    padding: 0 16px 0 6px;
    border: 0;
    border-radius: 19px;
    background: #fff;
    color: $site-background;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
    font: inherit;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: box-shadow 0.2s;

    &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
}

.studio-add-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: $site-background;
    color: #fff;

    // the icon's inline spans sit on the text baseline, which pushes it down
    ::v-deep .icon,
    ::v-deep .uk-icon,
    ::v-deep svg {
        display: block;
    }
}

.studio-sources {
    margin-bottom: 20px;
}

.studio-source {
    padding: 15px 5px;
    text-align: center;
}

// auto-fit: two options still share the whole width
.studio-options {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 10px;

    & > span {
        display: block;
    }
}

.studio-option {
    padding: 8px 0;
}

// bulk line

.studio-bulk {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-bottom: 20px;
}

// a pill showing the value, its uk-dropdown sits right after it

.studio-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.studio-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    max-width: 100%;
    height: 30px;
    padding: 0 9px 0 11px;
    border: 0;
    border-radius: 15px;
    background: #f0efeb;
    color: $site-background;
    font: inherit;
    font-size: 13px;
    white-space: nowrap;
    cursor: pointer;

    span {
        overflow: hidden;
        text-overflow: ellipsis;
    }

    &.is-strong {
        font-weight: 500;
    }

    &.is-ghost {
        background: none;
        box-shadow: inset 0 0 0 1px #dddcd6;
        color: $muted;
    }

    &:hover,
    &[aria-expanded='true'] {
        background: #e5e4df;
    }
}

.studio-bulk .studio-chip {
    background: #fff;
    box-shadow: inset 0 0 0 1px #e3e3e0;

    &:hover,
    &[aria-expanded='true'] {
        box-shadow: inset 0 0 0 1px $site-background;
    }
}

// every format with its papers and prices, picked in one go

.studio-dropdown {
    width: 340px;
    max-width: calc(100vw - 32px);
    max-height: 60vh;
    padding: 6px;
    border-radius: 14px;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.16);
    overflow-y: auto;

    &.is-narrow {
        width: 220px;

        // the filled items are stacked, not in the grid with its gap
        .studio-overview-paper + .studio-overview-paper {
            margin-top: 6px;
        }
    }

    // UIkit hides it only once it has picked up the new element; until then a
    // new tile (a copy, an upload) is the whole list high, and the copy's
    // scroll measured that and jumped
    &:not(.uk-open) {
        display: none;
    }
}

.studio-overview-row {
    padding: 6px 6px 8px 6px;

    & + & {
        border-top: 1px solid #f0efeb;
    }
}

.studio-overview-format {
    margin-bottom: 4px;
    padding: 0 6px;
    color: $site-background;
    font-size: 13px;
    font-weight: 500;
}

.studio-overview-papers {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    align-items: start;
    gap: 6px;
}

.studio-overview-paper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    padding: 7px 10px;
    border: 0;
    border-radius: 10px;
    background: #f0efeb;
    color: $site-background;
    font: inherit;
    font-size: 14px;
    text-align: left;
    cursor: pointer;

    small {
        color: $muted;
        font-size: 12px;
        white-space: nowrap;
    }

    &:hover {
        background: #e5e4df;
    }

    &.active {
        background: $site-background;
        color: #fff;

        small {
            color: rgba(255, 255, 255, 0.7);
        }

        // a black frame's swatch would vanish on the dark item
        .studio-swatch {
            box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.6);
        }

        .studio-overview-tiers {
            color: rgba(255, 255, 255, 0.7);
        }
    }

    // name and price on top (the price wraps under a long name), the tier
    // line below over the full width
    &.has-tiers {
        flex-wrap: wrap;
        align-items: baseline;
        row-gap: 0;

        small {
            margin-left: auto;
        }
    }

    .is-narrow & {
        justify-content: flex-start;
    }
}

// the top graduated price, one line
.studio-overview-tiers {
    flex-basis: 100%;
    margin-top: 2px;
    color: #1d7a58;
    font-size: 12px;
    line-height: 1.5;
}

.studio-swatch {
    flex: none;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.2);
}

// tiles: the print as it will look, its settings right below

.studio-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
    gap: 18px;
}

// two tiles only once both get 230px, on a phone one tile per row
@media (max-width: $breakpoint-xsmall-max) {
    .studio-grid {
        gap: 10px;
    }
}

.studio-tile {
    position: relative;
    display: flex;
    flex-direction: column;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    transition: box-shadow 0.2s;

    &:hover {
        box-shadow: 0 8px 22px rgba(0, 0, 0, 0.1);
    }

    &.has-warning {
        box-shadow: 0 0 0 2px $danger-color;
    }

    // a fresh copy: the same photo as its neighbour, the ring tells which
    &.is-new {
        box-shadow: 0 0 0 3px $info-color;
        transition: box-shadow 0.6s;
    }
}

.studio-tile-stage {
    position: relative;
    margin: 8px 8px 0 8px;
    padding-bottom: 80%;
    border-radius: 8px;
    background: #f3f2ee;
    overflow: hidden;
    cursor: pointer;
}

// the whole picture, the part that gets printed stays bright
.studio-crop-frame {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border-radius: 4px;
    background-color: #e5e5e5;
    background-repeat: no-repeat;
    background-size: 100% 100%;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15), 0 6px 14px rgba(0, 0, 0, 0.1);
    overflow: hidden;
}

.studio-crop-box {
    position: absolute;
    box-shadow: inset 0 0 0 2px #fff, 0 0 0 9999px rgba(18, 18, 18, 0.5);
}

.studio-skeleton {
    position: absolute;
    inset: 16%;
    border-radius: 4px;
    background: linear-gradient(90deg, #e9e9e9 0%, #f7f7f7 50%, #e9e9e9 100%);
    background-size: 200% 100%;
    animation: studio-shimmer 1.2s linear infinite;
}

@keyframes studio-shimmer {
    from {
        background-position: 200% 0;
    }
    to {
        background-position: -200% 0;
    }
}

.studio-tile-hover {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(18, 18, 18, 0.28);
    opacity: 0;
    transition: opacity 0.2s;

    span {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 7px 14px;
        border-radius: 17px;
        background: #fff;
        font-size: 14px;
        font-weight: 500;
    }

    .studio-tile-stage:hover & {
        opacity: 1;
    }
}

.studio-tile-tools {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 1;
    display: flex;
    gap: 6px;
    opacity: 0;
    transition: opacity 0.2s;

    button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 30px;
        height: 30px;
        border: 0;
        border-radius: 50%;
        background: #fff;
        color: $site-background;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
        cursor: pointer;
    }

    .studio-tile:hover &,
    .studio-tile.has-error & {
        opacity: 1;
    }

    // with a pointer the hover veil offers it
    .studio-tile-crop {
        display: none;
    }
}

@media (hover: none) {
    .studio-tile-hover {
        display: none;
    }

    .studio-tile-tools {
        opacity: 1;

        .studio-tile-crop {
            display: inline-flex;
        }
    }
}

.studio-flag {
    position: absolute;
    left: 8px;
    bottom: 10px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 500;

    &.warning {
        background: $warning-color;
        color: #3d3200;
    }

    &.danger {
        background: $danger-color;
        color: #fff;
    }
}

.studio-tile-progress {
    position: absolute;
    left: 10px;
    right: 10px;
    bottom: 4px;
    width: calc(100% - 20px);
    height: 4px !important;
    margin: 0 !important;
}

.studio-tile-error {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 12px;
    background: rgba(239, 39, 75, 0.94);
    color: #fff;
    font-size: 13px;
    font-weight: 500;
    text-align: center;
    cursor: default;
}

.studio-tile-body {
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 10px;
    padding: 12px 12px 12px 12px;
}

.studio-tile-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-top: auto;
    padding-top: 2px;
}

.studio-tile-price {
    text-align: right;
}

.studio-tier-hint {
    display: block;
    color: #1d7a58;
    font-size: 12px;
    white-space: nowrap;
}

.studio-stepper {
    display: inline-flex;
    align-items: center;
    height: 32px;
    border-radius: 16px;
    background: #f0efeb;
    overflow: hidden;

    button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 30px;
        height: 100%;
        border: 0;
        background: none;
        color: $site-background;
        cursor: pointer;

        &:hover:not(:disabled) {
            background: #e5e4df;
        }

        &:disabled {
            color: #ccc;
            cursor: not-allowed;
        }
    }

    input {
        width: 26px;
        height: 100%;
        border: 0;
        padding: 0;
        background: none;
        color: $site-background;
        font: inherit;
        font-weight: 500;
        text-align: center;
        -moz-appearance: textfield;

        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }
    }
}

@media (max-width: $breakpoint-xsmall-max) {
    .studio-tile-stage {
        margin: 6px 6px 0 6px;
        border-radius: 10px;
    }

    .studio-tile-body {
        padding: 10px 8px;
        gap: 8px;
    }

    .studio-tile-footer {
        flex-wrap: wrap;
    }
}

.studio-tile-add {
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 260px;
    border: 2px dashed #d4d4d0;
    background: transparent;
    box-shadow: none;
    color: $muted;
    font-weight: 500;
    text-align: center;
    cursor: pointer;
    transition: border-color 0.2s, color 0.2s;

    &:hover {
        border-color: $info-color;
        box-shadow: none;
        color: $info-color;
    }

    small {
        padding: 0 10px;
        font-size: 12px;
    }

    @media (max-width: $breakpoint-xsmall-max) {
        min-height: 120px;
    }
}

// order: as wide as the pictures and read top to bottom, the pay button
// right after the summary like LunaCart. Each block has its title on the
// left and its fields on the right

// no overflow: hidden, UIkit's scrollIntoView would take the card for a
// scroll container and drop the top bar offset for the blocks inside it
.studio-checkout-card {
    border-radius: 16px;
}

// on a phone the fields line up with the photos in the tiles above
@media (max-width: $breakpoint-xsmall-max) {
    .studio-checkout-card ::v-deep .uk-card-body {
        padding: 18px 8px;
    }
}

.studio-block {
    display: grid;
    grid-template-columns: 220px minmax(0, 1fr);
    column-gap: 40px;
    align-items: start;
    scroll-margin-top: $nav + 16px;

    & > * {
        grid-column: 2;
    }

    & > .studio-card-title {
        grid-column: 1;
        grid-row: 1 / span 20;
    }

    & + & {
        margin-top: 28px;
        padding-top: 28px;
        border-top: 1px solid $line;
    }
}

@media (max-width: $breakpoint-small-max) {
    .studio-block {
        grid-template-columns: minmax(0, 1fr);

        & > *,
        & > .studio-card-title {
            grid-column: 1;
            grid-row: auto;
        }
    }
}

// the generic components are only slightly rounded, here they match the tiles
.studio-checkout .studio-checkout-card {
    ::v-deep .card-select {
        border-radius: 14px;
    }

    ::v-deep .uk-input,
    ::v-deep .uk-select,
    ::v-deep .uk-textarea {
        border-radius: 12px;
    }
}

.paper-text {
    font-weight: 500;
    word-break: break-word;
}

.paper-price {
    font-size: 14px;
}

.badge {
    font-size: 13px;
    padding: 9px 11px;
    margin-top: -3px;
    margin-right: 5px;
    background-color: $info-color !important;
}

.studio-summary-row + .studio-summary-row {
    margin-top: 14px;
}

// fee and shipping stand apart from the pictures, like the total
.studio-summary-row:not(.is-extra) + .studio-summary-row.is-extra {
    margin-top: 22px;
}

.total-costs-text {
    font-weight: 500;
    margin-top: 22px !important;
}

.vat {
    font-size: 13px;
    margin-top: 10px;
    color: $muted;
}

.studio-payment-pick {
    margin-bottom: 12px;
    font-size: 14px;
}

.studio-pay-amount::before {
    content: '· ';
}

// on a phone the amount gets its own line instead of breaking anywhere
@media (max-width: $breakpoint-xsmall-max) {
    .studio-pay ::v-deep .uk-button {
        padding: 8px 20px !important;
        font-size: 16px !important;
        font-weight: 400 !important;
        line-height: 22px;
    }

    .studio-pay-amount {
        display: block;
        font-size: 14px;
        font-weight: 400;

        &::before {
            content: none;
        }
    }
}

.studio-terms {
    margin-bottom: 24px;

    ::v-deep .uk-checkbox {
        width: 22px;
        height: 22px;
    }
}

.studio-hint-tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-top: 6px;
    padding: 2px 10px;
    border-radius: 10px;
    background: rgba(43, 179, 130, 0.12);
    color: #1d7a58;
    font-size: 12px;
}

.studio-progress {
    height: 6px !important;
    margin: 6px 0 !important;
}

.studio-round {
    border-radius: $border-radius-next;
}

// success

.studio-success {
    max-width: 560px;
    margin: 40px auto 0 auto;
}

.studio-success-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 72px;
    height: 72px;
    margin-bottom: 18px;
    border-radius: 50%;
    background: $success-color;
    color: #fff;
}

// legal footer, always visible

.studio-footer {
    padding: 22px 0 28px 0;
    border-top: 1px solid $line;
    font-size: 14px;

    .studio-container {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: center;
        gap: 8px 20px;
    }
}

// crop view: an extended modal, the cropper on a dark stage inside it

.studio-editor {
    position: fixed;
    inset: 0;
    z-index: 1010;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 32px;
    background: rgba(18, 18, 18, 0.6);
}

.studio-editor-panel {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 1000px;
    height: 100%;
    max-height: 820px;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.3);
    overflow: hidden;

    // as high as its content, the prices list
    &.is-auto {
        max-width: 760px;
        height: auto;
        max-height: 100%;
    }
}

// the uk-modal animation: the backdrop fades, the panel slides in from above
.studio-editor-enter-active,
.studio-editor-leave-active {
    transition: opacity 0.15s linear;

    .studio-editor-panel {
        transition: opacity 0.3s linear, transform 0.3s linear;
    }
}

.studio-editor-enter,
.studio-editor-leave-to {
    opacity: 0;

    .studio-editor-panel {
        opacity: 0;
        transform: translateY(-100px);
    }
}

.studio-editor-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 16px 20px;
}

.studio-editor-title {
    min-width: 0;
    font-size: 17px;
    font-weight: 500;
}

.studio-editor-sub {
    color: $muted;
    font-size: 13px;
    font-weight: 400;
}

.studio-editor-nav {
    display: flex;
    flex: none;
    align-items: center;
    gap: 8px;

    // the tooltips sit on these wrappers: a disabled button gets no hover,
    // so it lets the pointer through to its span
    & > span {
        display: inline-flex;
    }

    button:disabled {
        pointer-events: none;
    }
}

.studio-editor-close {
    margin-left: 8px;
}

.studio-round-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: 0;
    border-radius: 50%;
    background: #f0efeb;
    color: $site-background;
    cursor: pointer;

    &:disabled {
        opacity: 0.35;
        cursor: not-allowed;
    }

    &:hover:not(:disabled) {
        background: #e5e4df;
    }
}

.studio-editor-stage {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    min-height: 0;
    margin: 0 20px;
    border-radius: 12px;
    background: #121212;
    overflow: hidden;
}

.studio-cropper {
    width: 100%;
    height: 100%;
}

.studio-editor-tools {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 16px 20px 20px 20px;
}

.studio-editor-hint {
    color: $muted;
    font-size: 13px;
}

.studio-segmented {
    display: inline-flex;
    padding: 3px;
    border-radius: 21px;
    background: #f0efeb;

    button {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        height: 34px;
        padding: 0 14px;
        border: 0;
        border-radius: 17px;
        background: none;
        color: $muted;
        font: inherit;
        font-size: 14px;
        cursor: pointer;

        &.active {
            background: #fff;
            color: $site-background;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
        }
    }

}

// the tile's frame chip, as high as the other tools in the row
.studio-editor-frame .studio-chip {
    height: 40px;
    padding: 0 14px 0 16px;
    border-radius: 20px;
    font-size: 14px;
}

.studio-soft-button {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 40px;
    padding: 0 16px;
    border: 0;
    border-radius: 20px;
    background: #f0efeb;
    color: $site-background;
    font: inherit;
    font-size: 14px;
    cursor: pointer;

    &:hover {
        background: #e5e4df;
    }
}

// the visible height: on phones the browser bar covers the bottom of 100vh
@supports (height: 100dvh) {
    .studio-editor {
        box-sizing: border-box;
        bottom: auto;
        height: 100dvh;
    }
}

@media (max-width: $breakpoint-xsmall-max) {
    .studio-editor {
        padding: 10px;
    }

    // the tools can take more room than is left, the panel scrolls to them
    .studio-editor-panel {
        overflow-y: auto;
    }

    .studio-editor-stage {
        flex: 1 0 300px;
    }

    .studio-editor-bar {
        padding: 12px;
    }

    .studio-editor-stage {
        margin: 0 10px;
    }
}

// drop overlay

.studio-dropoverlay {
    position: fixed;
    inset: 0;
    z-index: 1020;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: rgba(20, 24, 59, 0.85);

    * {
        pointer-events: none;
    }
}

.studio-dropoverlay-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    width: 100%;
    max-width: 600px;
    padding: 60px 24px;
    border: 3px dashed rgba(255, 255, 255, 0.7);
    border-radius: 16px;
    color: #fff;
    font-size: 22px;
    font-weight: 500;
    text-align: center;
}

// terminal file browser (unchanged)

.action-name {
    font-size: $site-font-size-large;
}

.terminal-content {
    min-height: calc(100vh - 40px - 50px - 48px - 60px - 53px);
}

.terminal-content-loading {
    min-height: calc(100vh - 105px - 50px - 48px - 40px - -17px);
}

.device-in-info {
    color: $site-background;
    font-size: $site-font-size-large;
}

.folder-struct {
    color: #000000 !important;
}

.folder-name {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
}

.uk-breadcrumb > :nth-child(n + 2):not(.uk-first-column)::before {
    margin: 0 10px 0 calc(10px - 4px) !important;
}

.image-item-struct {
    max-height: 150px;
    margin: auto;
}

.image-item {
    height: 100%;
    margin: auto;
}
</style>
